import {
  getApiBase,
  createWidgetSession,
  sendWidgetMessage,
  uploadWidgetMedia,
  toAbsoluteMediaUrl,
  getWidgetMessages,
  connectWidgetWebSocket,
  sendReaction,
} from "./api.js";

(function () {
  const scriptEl = document.currentScript;
  if (!scriptEl) return;

  const API_BASE = getApiBase();
  const publicKey = scriptEl.getAttribute("data-public-key");
  const themeRaw = scriptEl.getAttribute("data-theme");

  let theme = {};
  try {
    theme = themeRaw ? JSON.parse(themeRaw) : {};
  } catch (e) {
    theme = {};
  }

  let lang = (theme.language || "pt-BR").trim();
  let primary = theme.primaryColor || "#1f2937";
  let secondary = theme.secondaryColor || "#111827";
  let position = theme.position || "bottom-right";
  let configuredTitle = String(theme.title || "").trim();
  let title = configuredTitle || "Fale conosco";
  let hasCustomTitle = configuredTitle.length > 0;
  let fontFamily = theme.fontFamily || "Inter, Arial, sans-serif";
  let fontSize = theme.fontSize || "14px";
  let customClass = theme.customClass || "";
  let customCss = String(theme.customCss || "").trim();
  let welcomeMessage = String(theme.welcomeMessage || "").trim();
  let placeholderText = String(theme.placeholderText || "").trim();
  let offlineMessage = String(theme.offlineMessage || "").trim();
  let allowMedia = true;
  let allowAudio = true;
  let allowLiveVoice = false;
  let showBranding = true;
  const colorModeRaw = String(theme.colorMode || theme.mode || "light")
    .trim()
    .toLowerCase();
  const isDarkMode = colorModeRaw === "dark";

  let recaptchaSiteKey = String(theme.recaptchaSiteKey || "").trim();
  let recaptchaProvider = String(theme.recaptchaProvider || "recaptcha").trim();
  let widgetRecaptchaRequired = false;
  let recaptchaScriptPromise = null;

  const parseBoolAttr = (value, fallback = false) => {
    if (value === null || value === undefined || value === "") return fallback;
    const normalized = String(value).trim().toLowerCase();
    if (["1", "true", "yes", "y", "on", "sim"].includes(normalized)) return true;
    if (["0", "false", "no", "n", "off", "nao", "não"].includes(normalized))
      return false;
    return fallback;
  };

  const recaptchaDisabledByFlag = (() => {
    if (
      parseBoolAttr(scriptEl.getAttribute("data-recaptcha-disabled"), false) ||
      parseBoolAttr(scriptEl.getAttribute("data-disable-recaptcha"), false)
    ) {
      return true;
    }
    try {
      const w = typeof window !== "undefined" ? window : null;
      if (w && w.AGENTS_WIDGET_RECAPTCHA_DISABLED === true) return true;
      if (w && String(w.AGENTS_WIDGET_RECAPTCHA_ENABLED).toLowerCase() === "false")
        return true;
    } catch (_) {}
    return false;
  })();

  allowMedia = parseBoolAttr(theme.allowMedia, true);
  allowAudio = parseBoolAttr(theme.allowAudio, true);
  showBranding = parseBoolAttr(theme.showBranding, true);

  let autoStart = parseBoolAttr(
    scriptEl.getAttribute("data-auto-start"),
    parseBoolAttr(theme.autoStart, false),
  );
  let openOnLoad = parseBoolAttr(
    scriptEl.getAttribute("data-open-on-load"),
    parseBoolAttr(theme.openOnLoad, false),
  );
  let autoOpenDelay = (() => {
    const raw =
      scriptEl.getAttribute("data-auto-open-delay") ??
      theme.autoOpenDelay ??
      0;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  })();

  let collectName = parseBoolAttr(
    scriptEl.getAttribute("data-collect-name"),
    theme.collectName !== false,
  );
  let collectPhone = parseBoolAttr(
    scriptEl.getAttribute("data-collect-phone"),
    theme.collectPhone !== false,
  );
  let collectEmail = parseBoolAttr(
    scriptEl.getAttribute("data-collect-email"),
    theme.collectEmail === true,
  );
  let customFields = Array.isArray(theme.customFields) ? theme.customFields : [];

  const bypassPreform =
    parseBoolAttr(scriptEl.getAttribute("data-bypass-preform"), false) ||
    parseBoolAttr(scriptEl.getAttribute("data-skip-preform"), false);

  let introMessageTemplate =
    (theme.introMessage || "").trim() ||
    "Meus dados:\nNome: {name}\nTelefone: {phone}\nEmail: {email}\n{message}";

  const presetName = (scriptEl.getAttribute("data-user-name") || "").trim();
  const presetPhone = (scriptEl.getAttribute("data-user-phone") || "").trim();
  const presetEmail = (scriptEl.getAttribute("data-user-email") || "").trim();
  const presetContextRaw = scriptEl.getAttribute("data-user-context");
  let presetContext = {};
  try {
    presetContext = presetContextRaw ? JSON.parse(presetContextRaw) : {};
  } catch (_) {
    presetContext = {};
  }

  function parseUrlInitial() {
    const result = {
      open: false,
      message: null,
      defaultMessage: "",
      fullscreen: false,
      hideClose: false,
      profile: { name: "", phone: "", email: "" },
      context: {},
    };
    try {
      if (typeof window === "undefined" || !window.location) return result;
      const params = new URLSearchParams(window.location.search || "");
      const truthy = (raw) => {
        const v = String(raw || "").toLowerCase().trim();
        return v === "1" || v === "true" || v === "open" || v === "yes";
      };
      const widgetParam = params.get("widget");
      const openParam = params.get("open");
      const chatParam = params.get("chat");
      const chatTextParam =
        params.get("chat-text") ||
        params.get("chat_text") ||
        params.get("default-message") ||
        params.get("default_message");
      if (chatTextParam !== null) {
        result.defaultMessage = String(chatTextParam || "").trim();
      }
      result.fullscreen =
        truthy(params.get("fullscreen")) ||
        truthy(params.get("full-screen")) ||
        truthy(params.get("full_screen"));
      result.hideClose =
        truthy(params.get("hide-close")) ||
        truthy(params.get("hide_close")) ||
        truthy(params.get("hideclose"));
      if (
        (widgetParam !== null && truthy(widgetParam)) ||
        (openParam !== null && truthy(openParam)) ||
        (chatParam !== null && truthy(chatParam))
      ) {
        result.open = true;
      }
      for (const [key, value] of params.entries()) {
        const m =
          key.match(/^initial\[([^\]]+)\]$/) ||
          key.match(/^initial\.(.+)$/);
        if (!m) continue;
        const subKey = String(m[1] || "").trim();
        if (!subKey) continue;
        const lower = subKey.toLowerCase().replace(/[_-]/g, "");
        const trimmed = String(value || "").trim();
        if (lower === "message" || lower === "msg") {
          result.message = trimmed || "Olá!";
          result.open = true;
        } else if (lower === "clientname" || lower === "name") {
          result.profile.name = trimmed;
        } else if (lower === "clientphone" || lower === "phone") {
          result.profile.phone = trimmed;
        } else if (lower === "clientemail" || lower === "email") {
          result.profile.email = trimmed;
        } else if (
          lower === "clientcompany" ||
          lower === "company" ||
          lower === "companyname"
        ) {
          result.context.client_company = trimmed;
        } else {
          result.context[subKey] = trimmed;
        }
      }
    } catch (_) {}
    return result;
  }

  const urlInitial = parseUrlInitial();
  const dataDefaultMessage = (
    scriptEl.getAttribute("data-default-message") || ""
  ).trim();
  let configuredDefaultMessage =
    urlInitial.defaultMessage ||
    dataDefaultMessage ||
    String(theme.preformDefaultMessage || "").trim();
  let fullScreen =
    urlInitial.fullscreen ||
    parseBoolAttr(
      scriptEl.getAttribute("data-fullscreen") ||
        scriptEl.getAttribute("data-full-screen"),
      parseBoolAttr(theme.fullScreen, false),
    );
  let hideClose =
    urlInitial.hideClose ||
    parseBoolAttr(
      scriptEl.getAttribute("data-hide-close") ||
        scriptEl.getAttribute("data-hideclose"),
      parseBoolAttr(theme.hideClose, false),
    );

  function year() {
    return new Date().getFullYear();
  }

  function t(key, fallback) {
    return (window.__IAW_I18N && window.__IAW_I18N[key]) || fallback || key;
  }

  function loadI18n() {
    const scriptSrc = scriptEl && scriptEl.src ? scriptEl.src : "";
    const baseFromScript = scriptSrc ? scriptSrc.replace(/[^/]+$/, "") : "";
    const i18nBase =
      window.AGENTS_WIDGET_I18N_BASE ||
      (baseFromScript ? baseFromScript + "i18n/" : "i18n/");
    return fetch(i18nBase + `${lang}.json`)
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }

  // =============================================================================
  // TRIGGERS SYSTEM
  // =============================================================================

  const STORAGE_KEY = "iaw_triggers";
  const VISIT_KEY = "iaw_visits";

  let widgetTriggers = [];
  let triggersInitialized = false;
  let panelRef = null;
  let launcherRef = null;

  function getTriggerState() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
    } catch {
      return {};
    }
  }

  function setTriggerFired(triggerId) {
    const state = getTriggerState();
    state[triggerId] = { fired: true, timestamp: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  function shouldFireTrigger(trigger) {
    const oncePerSession = trigger.conditions?.once_per_session !== false;
    if (!oncePerSession) return true;
    const state = getTriggerState();
    return !state[trigger.id]?.fired;
  }

  function getVisitCount() {
    try {
      const visits = JSON.parse(localStorage.getItem(VISIT_KEY) || "{}");
      return {
        total: visits.total || 0,
        today: visits.today || 0,
        lastDate: visits.lastDate || null,
      };
    } catch {
      return { total: 0, today: 0, lastDate: null };
    }
  }

  function incrementVisitCount() {
    const visits = getVisitCount();
    const today = new Date().toDateString();

    visits.total++;
    if (visits.lastDate === today) {
      visits.today++;
    } else {
      visits.today = 1;
      visits.lastDate = today;
    }

    localStorage.setItem(VISIT_KEY, JSON.stringify(visits));
    return visits;
  }

  function matchUrl(pattern, url = window.location.pathname) {
    if (!pattern || pattern === "*") return true;

    // Simple glob matching
    const regex = new RegExp(
      "^" + pattern.replace(/\*/g, ".*").replace(/\?/g, ".") + "$",
    );
    return regex.test(url);
  }

  function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  }

  function getDeviceType() {
    if (/iPad|Tablet/i.test(navigator.userAgent)) return "tablet";
    if (isMobile()) return "mobile";
    return "desktop";
  }

  function getUtmParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
    };
  }

  function executeTriggerAction(trigger, panel, launcher) {
    if (!shouldFireTrigger(trigger)) return;

    const { action_type, action_config } = trigger;
    const delay = (action_config?.delay_after_trigger || 0) * 1000;

    setTimeout(() => {
      setTriggerFired(trigger.id);

      // Apply animation to launcher
      if (action_config?.animation && action_config.animation !== "none") {
        applyAnimation(launcher, action_config.animation);
      }

      // Play sound if enabled
      if (action_config?.sound_enabled) {
        playNotificationSound();
      }

      // Open widget and/or show message
      if (action_type === "open_widget" || action_type === "both") {
        panel.style.display = "flex";
      }

      if (
        (action_type === "show_message" || action_type === "both") &&
        action_config?.message
      ) {
        showProactiveMessage(action_config.message, panel);
      }
    }, delay);
  }

  function applyAnimation(element, animation) {
    const animations = {
      bounce: "iaw-bounce",
      shake: "iaw-shake",
      pulse: "iaw-pulse",
    };

    const className = animations[animation];
    if (className) {
      element.classList.add(className);
      setTimeout(() => element.classList.remove(className), 1000);
    }
  }

  function playNotificationSound() {
    try {
      const audio = new Audio(
        "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQYAj+3teleQYBgGP5ra1+WJBgCP7d6V5BgGP5ra1+WJBgCP7d6V5BgGP5ra1+WJBgCP7d6V5BgGP5ra1+WJBgCP7d6V5Bg=",
      );
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch {}
  }

  function showProactiveMessage(message, panel) {
    const messagesContainer = panel.querySelector(".iaw-messages");
    if (!messagesContainer) return;

    const row = document.createElement("div");
    row.className = "iaw-row iaw-agent iaw-proactive";
    const bubble = document.createElement("div");
    bubble.className = "iaw-bubble";
    bubble.innerHTML = message;
    const time = document.createElement("span");
    time.className = "iaw-time";
    time.textContent = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    bubble.appendChild(time);
    row.appendChild(bubble);
    messagesContainer.appendChild(row);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  // =============================================================================
  // TRIGGER LISTENERS
  // =============================================================================

  function initTimeOnPageTrigger(trigger, panel, launcher) {
    const { seconds, url_pattern } = trigger.conditions;
    if (!matchUrl(url_pattern)) return;

    setTimeout(
      () => {
        executeTriggerAction(trigger, panel, launcher);
      },
      (seconds || 30) * 1000,
    );
  }

  function initScrollDepthTrigger(trigger, panel, launcher) {
    const { percentage, url_pattern } = trigger.conditions;
    if (!matchUrl(url_pattern)) return;

    let fired = false;
    const handler = () => {
      if (fired) return;
      const scrolled =
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) *
        100;
      if (scrolled >= (percentage || 50)) {
        fired = true;
        executeTriggerAction(trigger, panel, launcher);
        window.removeEventListener("scroll", handler);
      }
    };

    window.addEventListener("scroll", handler, { passive: true });
  }

  function initExitIntentTrigger(trigger, panel, launcher) {
    const { sensitivity, url_pattern, mobile_enabled } = trigger.conditions;
    if (!matchUrl(url_pattern)) return;
    if (isMobile() && !mobile_enabled) return;

    let fired = false;
    const threshold =
      sensitivity === "high" ? 20 : sensitivity === "low" ? 5 : 10;

    const handler = (e) => {
      if (fired) return;
      if (e.clientY < threshold) {
        fired = true;
        executeTriggerAction(trigger, panel, launcher);
        document.removeEventListener("mouseout", handler);
      }
    };

    document.addEventListener("mouseout", handler);
  }

  function initUrlMatchTrigger(trigger, panel, launcher) {
    const { url_patterns, match_type } = trigger.conditions;
    if (!url_patterns || url_patterns.length === 0) return;

    const currentUrl = window.location.pathname + window.location.search;
    let matched = false;

    for (const pattern of url_patterns) {
      if (match_type === "exact" && currentUrl === pattern) {
        matched = true;
        break;
      } else if (match_type === "contains" && currentUrl.includes(pattern)) {
        matched = true;
        break;
      } else if (match_type === "regex") {
        try {
          if (new RegExp(pattern).test(currentUrl)) {
            matched = true;
            break;
          }
        } catch {}
      } else if (matchUrl(pattern, currentUrl)) {
        matched = true;
        break;
      }
    }

    if (matched) {
      executeTriggerAction(trigger, panel, launcher);
    }
  }

  function initVisitCountTrigger(trigger, panel, launcher) {
    const { min_visits, max_visits, count_type } = trigger.conditions;
    const visits = getVisitCount();

    let count = visits.total;
    if (count_type === "today") count = visits.today;
    else if (count_type === "session") count = 1; // Session is always 1 for current session

    if (
      count >= (min_visits || 1) &&
      (max_visits === null || count <= max_visits)
    ) {
      executeTriggerAction(trigger, panel, launcher);
    }
  }

  function initScheduleTrigger(trigger, panel, launcher) {
    const { days, start_time, end_time, invert } = trigger.conditions;

    const now = new Date();
    const dayNames = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const currentDay = dayNames[now.getDay()];
    const currentTime = now.toTimeString().slice(0, 5);

    const isDayMatch = (days || []).includes(currentDay);
    const isTimeMatch =
      currentTime >= (start_time || "00:00") &&
      currentTime <= (end_time || "23:59");

    let shouldFire = isDayMatch && isTimeMatch;
    if (invert) shouldFire = !shouldFire;

    if (shouldFire) {
      executeTriggerAction(trigger, panel, launcher);
    }
  }

  function initDeviceTrigger(trigger, panel, launcher) {
    const { device_types, match_type } = trigger.conditions;
    const currentDevice = getDeviceType();

    const isMatch = (device_types || []).includes(currentDevice);
    const shouldFire = match_type === "exclude" ? !isMatch : isMatch;

    if (shouldFire) {
      executeTriggerAction(trigger, panel, launcher);
    }
  }

  function initUtmSourceTrigger(trigger, panel, launcher) {
    const { utm_source, utm_medium, utm_campaign, match_type } =
      trigger.conditions;
    const params = getUtmParams();

    const checks = [];
    if (utm_source?.length) checks.push(utm_source.includes(params.utm_source));
    if (utm_medium?.length) checks.push(utm_medium.includes(params.utm_medium));
    if (utm_campaign?.length)
      checks.push(utm_campaign.includes(params.utm_campaign));

    if (checks.length === 0) return;

    const shouldFire =
      match_type === "all" ? checks.every(Boolean) : checks.some(Boolean);

    if (shouldFire) {
      executeTriggerAction(trigger, panel, launcher);
    }
  }

  function initTriggerListeners(triggers, panel, launcher) {
    // Sort by priority (higher first)
    const sortedTriggers = [...triggers].sort(
      (a, b) => (b.priority || 0) - (a.priority || 0),
    );

    for (const trigger of sortedTriggers) {
      if (!trigger.is_active) continue;

      switch (trigger.trigger_type) {
        case "time_on_page":
          initTimeOnPageTrigger(trigger, panel, launcher);
          break;
        case "scroll_depth":
          initScrollDepthTrigger(trigger, panel, launcher);
          break;
        case "exit_intent":
          initExitIntentTrigger(trigger, panel, launcher);
          break;
        case "url_match":
          initUrlMatchTrigger(trigger, panel, launcher);
          break;
        case "visit_count":
          initVisitCountTrigger(trigger, panel, launcher);
          break;
        case "schedule":
          initScheduleTrigger(trigger, panel, launcher);
          break;
        case "device":
          initDeviceTrigger(trigger, panel, launcher);
          break;
        case "utm_source":
          initUtmSourceTrigger(trigger, panel, launcher);
          break;
      }
    }
  }

  async function loadWidgetConfig() {
    try {
      const response = await fetch(
        `${API_BASE}/widget/config/${encodeURIComponent(publicKey)}`,
      );
      if (response.ok) {
        const data = await response.json();
        widgetTriggers = data.triggers || [];
        const config = data.config || {};
        const visual = config.visual && typeof config.visual === "object" ? config.visual : {};
        const features = config.features && typeof config.features === "object" ? config.features : {};
        const messages = config.messages && typeof config.messages === "object" ? config.messages : {};

        const pick = (...values) => {
          for (const value of values) {
            if (value === null || value === undefined) continue;
            const text = String(value).trim();
            if (text) return text;
          }
          return "";
        };
        const pickBool = (current, ...values) => {
          for (const value of values) {
            if (value === null || value === undefined || value === "") continue;
            return parseBoolAttr(value, current);
          }
          return current;
        };

        const serverTitle = pick(visual.title, config.title);
        if (serverTitle) {
          configuredTitle = serverTitle;
          title = serverTitle;
          hasCustomTitle = true;
        }
        primary = pick(visual.primaryColor, config.primaryColor) || primary;
        secondary = pick(visual.secondaryColor, config.secondaryColor) || secondary;
        position = pick(visual.position, config.position) || position;
        fontFamily = pick(visual.fontFamily, config.fontFamily) || fontFamily;
        fontSize = pick(visual.fontSize, config.fontSize) || fontSize;
        customClass = pick(visual.customClass, config.customClass) || customClass;
        customCss = pick(visual.customCss, config.customCss) || customCss;
        lang = pick(visual.language, config.language) || lang;

        allowMedia = pickBool(allowMedia, features.allowMedia, config.allowMedia);
        allowAudio = pickBool(allowAudio, features.allowAudio, config.allowAudio);
        const liveBlock = data.live && typeof data.live === "object" ? data.live : {};
        allowLiveVoice = !!(
          liveBlock.enabled &&
          liveBlock.available &&
          pickBool(
            false,
            features.liveEnabled,
            config.liveEnabled,
            liveBlock.enabled,
          )
        );
        showBranding = pickBool(showBranding, visual.showBranding, config.showBranding);
        collectName = pickBool(collectName, features.collectName, config.collectName);
        collectPhone = pickBool(collectPhone, features.collectPhone, config.collectPhone);
        collectEmail = pickBool(collectEmail, features.collectEmail, config.collectEmail);
        autoStart = pickBool(autoStart, features.autoStart, config.autoStart);
        openOnLoad = pickBool(openOnLoad, features.openOnLoad, config.openOnLoad);
        fullScreen = pickBool(fullScreen, features.fullScreen, config.fullScreen);
        hideClose = pickBool(hideClose, features.hideClose, config.hideClose);
        if (Array.isArray(features.customFields)) customFields = features.customFields;
        else if (Array.isArray(config.customFields)) customFields = config.customFields;

        welcomeMessage = pick(messages.welcomeMessage, config.welcomeMessage, config.welcome_message) || welcomeMessage;
        placeholderText = pick(messages.placeholderText, config.placeholderText, config.placeholder_text) || placeholderText;
        offlineMessage = pick(messages.offlineMessage, config.offlineMessage, config.offline_message) || offlineMessage;
        configuredDefaultMessage =
          urlInitial.defaultMessage ||
          dataDefaultMessage ||
          pick(
            messages.preformDefaultMessage,
            config.preformDefaultMessage,
            config.preform_default_message,
          ) ||
          configuredDefaultMessage;
        introMessageTemplate =
          pick(messages.introMessage, config.introMessage, config.intro_message) ||
          introMessageTemplate;
        const serverAutoOpenDelay = Number(messages.autoOpenDelay ?? config.autoOpenDelay ?? config.auto_open_delay);
        if (Number.isFinite(serverAutoOpenDelay) && serverAutoOpenDelay > 0) {
          autoOpenDelay = serverAutoOpenDelay;
        }
        if (data.recaptcha_site_key) {
          recaptchaSiteKey = String(data.recaptcha_site_key).trim();
        }
        if (data.recaptcha_provider) {
          recaptchaProvider = String(data.recaptcha_provider).trim();
        }
        widgetRecaptchaRequired = !!data.widget_recaptcha_enabled;
        if (recaptchaDisabledByFlag) {
          widgetRecaptchaRequired = false;
        }
        return data;
      }
    } catch (e) {
      console.warn("Failed to load widget config:", e);
    }
    return null;
  }

  function _getRecaptchaClient() {
    const isEnterprise = recaptchaProvider === "recaptcha_enterprise";
    return isEnterprise
      ? window.grecaptcha && window.grecaptcha.enterprise
      : window.grecaptcha;
  }

  function ensureRecaptchaReady() {
    if (recaptchaDisabledByFlag) return Promise.resolve(null);
    if (!widgetRecaptchaRequired || !recaptchaSiteKey)
      return Promise.resolve(null);
    const isEnterprise = recaptchaProvider === "recaptcha_enterprise";
    const recaptchaClient = _getRecaptchaClient();
    if (recaptchaClient && recaptchaClient.execute) {
      return new Promise((resolve) => {
        try {
          recaptchaClient.ready(() => resolve(true));
        } catch (_) {
          resolve(true);
        }
      });
    }
    if (recaptchaScriptPromise) return recaptchaScriptPromise;
    recaptchaScriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      const scriptName = isEnterprise ? "enterprise.js" : "api.js";
      s.src = `https://www.google.com/recaptcha/${scriptName}?render=${encodeURIComponent(
        recaptchaSiteKey,
      )}`;
      s.async = true;
      s.onload = () => {
        try {
          const loadedClient = _getRecaptchaClient();
          if (!loadedClient || !loadedClient.ready) {
            reject(new Error("recaptcha_client_unavailable"));
            return;
          }
          loadedClient.ready(() => resolve(true));
        } catch (e) {
          reject(e);
        }
      };
      s.onerror = () => reject(new Error("recaptcha_load_failed"));
      document.head.appendChild(s);
    });
    return recaptchaScriptPromise;
  }

  async function getRecaptchaToken(action) {
    if (recaptchaDisabledByFlag) return null;
    if (!widgetRecaptchaRequired || !recaptchaSiteKey) return null;
    try {
      await ensureRecaptchaReady();
      const client = _getRecaptchaClient();
      if (!client || !client.execute) return null;
      return await client.execute(recaptchaSiteKey, { action });
    } catch (_) {
      return null;
    }
  }

  // =============================================================================
  // MAIN WIDGET INITIALIZATION
  // =============================================================================

  loadI18n().then(async (dict) => {
    window.__IAW_I18N = dict || {};

    await loadWidgetConfig();
    if (!document.body) {
      await new Promise((resolve) => {
        document.addEventListener("DOMContentLoaded", resolve, { once: true });
      });
    }

    // Increment visit count
    incrementVisitCount();

    const styles = `
      .iaw-launcher,.iaw-panel{box-sizing:border-box}
      .iaw-launcher *,.iaw-panel *{box-sizing:border-box}
      .iaw-launcher{
        --iaw-primary:${primary};
        --iaw-secondary:${secondary};
        --iaw-accent:#f7941d;
        position:fixed;z-index:999999;display:inline-flex;align-items:center;gap:10px;
        padding:12px 18px 12px 14px;border:0;border-radius:999px;
        background:linear-gradient(135deg,var(--iaw-primary),var(--iaw-secondary));
        color:#fff;font-family:${fontFamily};font-size:${fontSize};font-weight:600;
        cursor:pointer;letter-spacing:-.005em;
        box-shadow:0 16px 36px -10px rgba(15,23,42,.38),0 6px 14px -2px rgba(15,23,42,.22);
        transition:transform .25s ease,box-shadow .25s ease,filter .25s ease;
      }
      .iaw-launcher:hover{transform:translateY(-2px);filter:brightness(1.05);box-shadow:0 24px 48px -12px rgba(15,23,42,.42),0 8px 18px -2px rgba(15,23,42,.26)}
      .iaw-launcher:focus-visible{outline:3px solid rgba(247,148,29,.5);outline-offset:3px}
      .iaw-launcher .iaw-launcher-icon{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:999px;background:rgba(255,255,255,.18)}
      .iaw-launcher .iaw-launcher-icon svg{width:18px;height:18px;display:block}
      .iaw-launcher .iaw-launcher-pulse{position:absolute;top:-2px;right:-2px;width:14px;height:14px;border-radius:999px;background:var(--iaw-accent);border:2px solid #fff;animation:iaw-launcher-pulse 2.4s infinite}
      @keyframes iaw-launcher-pulse{0%{box-shadow:0 0 0 0 rgba(247,148,29,.55)}70%{box-shadow:0 0 0 14px rgba(247,148,29,0)}100%{box-shadow:0 0 0 0 rgba(247,148,29,0)}}

      .iaw-panel{
        --iaw-primary:${primary};
        --iaw-secondary:${secondary};
        --iaw-accent:#f7941d;
        --iaw-accent-strong:#c66200;
        --iaw-bg:${isDarkMode ? "#0f172a" : "#f4f6f9"};
        --iaw-surface:${isDarkMode ? "#1e293b" : "#ffffff"};
        --iaw-text:${isDarkMode ? "#e5e7eb" : "#15181d"};
        --iaw-muted:${isDarkMode ? "#94a3b8" : "#6b7480"};
        --iaw-line:${isDarkMode ? "rgba(148,163,184,.28)" : "rgba(15,23,42,.08)"};
        --iaw-line-strong:${isDarkMode ? "rgba(148,163,184,.4)" : "rgba(15,23,42,.14)"};
        --iaw-bubble-agent:${isDarkMode ? "#243044" : "#ffffff"};
        --iaw-bubble-agent-text:${isDarkMode ? "#e5e7eb" : "#1f2733"};
        --iaw-radius:22px;
        position:fixed;z-index:999999;width:380px;max-width:calc(100vw - 28px);height:620px;max-height:calc(100vh - 96px);
        background:var(--iaw-surface);border-radius:var(--iaw-radius);
        box-shadow:${
          isDarkMode
            ? "0 32px 80px -22px rgba(0,0,0,.55),0 12px 28px -10px rgba(0,0,0,.35)"
            : "0 32px 80px -22px rgba(15,23,42,.35),0 12px 28px -10px rgba(15,23,42,.18)"
        };
        display:none;flex-direction:column;overflow:hidden;border:1px solid var(--iaw-line);
        font-family:${fontFamily};font-size:${fontSize};color:var(--iaw-text);
        animation:iaw-panel-in .26s ease both;
      }
      .iaw-panel.iaw-fullscreen{
        inset:0 !important;right:0 !important;bottom:0 !important;left:0 !important;top:0 !important;
        width:100vw;height:100vh;height:100dvh;max-width:none;max-height:none;border-radius:0;border:0;box-shadow:none;
      }
      .iaw-panel.iaw-hide-close .iaw-close{display:none}
      @keyframes iaw-panel-in{from{opacity:0;transform:translateY(10px) scale(.985)}to{opacity:1;transform:none}}

      .iaw-header{position:relative;padding:16px 18px;color:#fff;background:linear-gradient(135deg,var(--iaw-primary),var(--iaw-secondary));display:flex;align-items:center;justify-content:space-between;gap:12px;flex-shrink:0}
      .iaw-header::after{content:"";position:absolute;left:0;right:0;bottom:-12px;height:12px;background:linear-gradient(180deg,rgba(15,23,42,.16),transparent);pointer-events:none}
      .iaw-header-info{display:flex;align-items:center;gap:12px;min-width:0;flex:1}
      .iaw-avatar{display:grid;place-items:center;width:38px;height:38px;border-radius:13px;background:rgba(255,255,255,.18);font-size:13px;font-weight:800;letter-spacing:.04em;flex-shrink:0}
      .iaw-avatar svg{width:18px;height:18px}
      .iaw-header-text{min-width:0;line-height:1.15}
      .iaw-header-text strong{display:block;font-size:15px;font-weight:700;letter-spacing:-.01em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
      .iaw-header-text span{display:inline-flex;align-items:center;gap:6px;font-size:12px;opacity:.88;margin-top:3px;font-weight:500}
      .iaw-online-dot{width:8px;height:8px;border-radius:999px;background:#22c55e;box-shadow:0 0 0 3px rgba(34,197,94,.22)}
      .iaw-close{width:34px;height:34px;border-radius:11px;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;background:rgba(255,255,255,.14);color:#fff;border:none;transition:background .2s ease,transform .2s ease;flex-shrink:0;padding:0}
      .iaw-close:hover{background:rgba(255,255,255,.24);transform:scale(1.05)}
      .iaw-close:focus-visible{outline:2px solid #fff;outline-offset:2px}
      .iaw-close svg{width:14px;height:14px;display:block}

      .iaw-body{flex:1;min-height:0;display:flex;flex-direction:column;background:var(--iaw-bg)}
      .iaw-messages{flex:1;min-height:0;overflow-y:auto;overflow-x:hidden;padding:18px 16px 12px;scroll-behavior:smooth;scrollbar-width:thin;scrollbar-color:rgba(15,23,42,.18) transparent;overscroll-behavior:contain}
      .iaw-messages::-webkit-scrollbar{width:6px;height:0}
      .iaw-messages::-webkit-scrollbar-thumb{background:rgba(15,23,42,.18);border-radius:999px}
      .iaw-messages::-webkit-scrollbar-track{background:transparent}

      .iaw-row{display:flex;margin:4px 0;align-items:flex-end;gap:4px}
      .iaw-row + .iaw-row{margin-top:6px}

      .iaw-bubble{max-width:80%;padding:11px 14px;border-radius:18px;white-space:pre-wrap;line-height:1.42;word-wrap:break-word;overflow-wrap:anywhere;word-break:break-word;position:relative;box-shadow:0 1px 2px rgba(15,23,42,.04);animation:iaw-bubble-in .2s ease both}
      .iaw-bubble img,.iaw-bubble video,.iaw-bubble audio{max-width:100%;height:auto;display:block}
      @keyframes iaw-bubble-in{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
      .iaw-me{justify-content:flex-end}
      .iaw-me .iaw-bubble{background:linear-gradient(135deg,var(--iaw-primary),var(--iaw-secondary));color:#fff;border-bottom-right-radius:6px}
      .iaw-me .iaw-bubble a{color:#fff;text-decoration:underline;text-decoration-color:rgba(255,255,255,.5)}
      .iaw-agent .iaw-bubble{background:var(--iaw-bubble-agent);border:1px solid var(--iaw-line);border-bottom-left-radius:6px;color:var(--iaw-bubble-agent-text)}
      .iaw-agent .iaw-bubble a{color:var(--iaw-accent-strong);font-weight:600}
      .iaw-system .iaw-bubble{background:#fff8eb;border:1px solid #fde6b1;color:#92651a}
      .iaw-proactive .iaw-bubble{background:linear-gradient(135deg,#fff5e6,#ffe9c8);border:1px solid #f9d699;color:#8a4d12;box-shadow:0 8px 18px -10px rgba(247,148,29,.45)}
      .iaw-time{display:block;margin-top:4px;font-size:10.5px;opacity:.7;font-weight:500}
      .iaw-me .iaw-time{color:rgba(255,255,255,.85);opacity:.95}
      .iaw-agent .iaw-time{color:var(--iaw-muted)}

      .iaw-input{flex-shrink:0;display:flex;align-items:center;gap:8px;padding:12px;border-top:1px solid var(--iaw-line);background:var(--iaw-surface);position:relative}
      .iaw-input input{flex:1;min-width:0;padding:11px 14px;border:1px solid var(--iaw-line);border-radius:14px;outline:none;font:inherit;color:var(--iaw-text);background:var(--iaw-bg);transition:border-color .2s ease,box-shadow .2s ease,background .2s ease}
      .iaw-input input::placeholder{color:var(--iaw-muted)}
      .iaw-input input:focus{border-color:var(--iaw-accent);background:var(--iaw-surface);box-shadow:0 0 0 3px rgba(247,148,29,.14)}

      .iaw-toolbar{display:flex;align-items:center;gap:6px}
      .iaw-btn{width:38px;height:38px;display:inline-flex;align-items:center;justify-content:center;border:none;border-radius:12px;cursor:pointer;background:var(--iaw-bg);color:${isDarkMode ? "#94a3b8" : "#4a525d"};transition:background .2s ease,color .2s ease,transform .2s ease,box-shadow .2s ease;padding:0}
      .iaw-btn:hover{background:${isDarkMode ? "rgba(148,163,184,.14)" : "rgba(15,23,42,.07)"};color:var(--iaw-text)}
      .iaw-btn:focus-visible{outline:2px solid rgba(247,148,29,.45);outline-offset:2px}
      .iaw-btn svg{width:18px;height:18px;display:block}
      .iaw-btn.primary{background:linear-gradient(135deg,var(--iaw-accent),var(--iaw-accent-strong));color:#fff;box-shadow:0 10px 22px -8px rgba(247,148,29,.6)}
      .iaw-btn.primary:hover{transform:translateY(-1px);box-shadow:0 14px 28px -10px rgba(247,148,29,.65)}
      .iaw-btn.primary svg{width:16px;height:16px}
      .iaw-btn.is-recording{background:#fef2f2;color:#b91c1c;box-shadow:0 0 0 3px rgba(239,68,68,.18)}
      .iaw-btn-live{color:var(--iaw-accent-strong)}
      .iaw-btn-live.is-active{background:rgba(247,148,29,.14);color:var(--iaw-accent-strong);box-shadow:0 0 0 3px rgba(247,148,29,.18)}

      .iaw-live-overlay{position:absolute;inset:0;z-index:50;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.72);backdrop-filter:blur(6px);padding:20px}
      .iaw-live-card{width:min(100%,280px);background:var(--iaw-surface);border-radius:20px;padding:28px 22px 22px;text-align:center;box-shadow:0 24px 48px -16px rgba(0,0,0,.45);border:1px solid var(--iaw-line)}
      .iaw-live-eq{display:flex;align-items:flex-end;justify-content:center;gap:5px;height:36px;margin-bottom:16px}
      .iaw-live-eq span{width:5px;border-radius:999px;background:linear-gradient(180deg,var(--iaw-accent),var(--iaw-accent-strong));animation:iaw-live-bar 1s ease-in-out infinite}
      .iaw-live-eq span:nth-child(1){height:12px;animation-delay:0s}
      .iaw-live-eq span:nth-child(2){height:22px;animation-delay:.1s}
      .iaw-live-eq span:nth-child(3){height:30px;animation-delay:.2s}
      .iaw-live-eq span:nth-child(4){height:18px;animation-delay:.3s}
      .iaw-live-eq span:nth-child(5){height:14px;animation-delay:.4s}
      .iaw-live-overlay[data-state="speaking"] .iaw-live-eq span{animation-duration:.55s}
      .iaw-live-overlay[data-state="connecting"] .iaw-live-eq span{opacity:.45}
      .iaw-live-overlay[data-state="error"] .iaw-live-eq span{background:#ef4444;animation:none;height:8px}
      @keyframes iaw-live-bar{0%,100%{transform:scaleY(.45)}50%{transform:scaleY(1)}}
      .iaw-live-status{font-size:14px;font-weight:600;color:var(--iaw-text);margin-bottom:8px}
      .iaw-live-error{font-size:12px;color:#b91c1c;margin-bottom:12px;line-height:1.4}
      .iaw-live-hangup{border:none;border-radius:12px;padding:12px 18px;cursor:pointer;background:#dc2626;color:#fff;font:inherit;font-weight:700;width:100%}
      .iaw-live-hangup:hover{background:#b91c1c}
      .iaw-panel.iaw-live-active .iaw-input{pointer-events:none;opacity:.45}
      .iaw-live-remote{display:none}

      .iaw-emoji{position:absolute;bottom:calc(100% + 8px);left:12px;background:#fff;border:1px solid var(--iaw-line);border-radius:14px;box-shadow:0 16px 32px -10px rgba(15,23,42,.22);padding:8px;display:none;flex-wrap:wrap;max-width:240px;z-index:1000000}
      .iaw-emoji button{border:none;background:transparent;font-size:20px;cursor:pointer;padding:6px 8px;border-radius:8px;transition:background .15s ease,transform .15s ease}
      .iaw-emoji button:hover{background:rgba(15,23,42,.06);transform:scale(1.08)}

      .iaw-typing{flex-shrink:0;font-size:12px;color:var(--iaw-muted);padding:2px 18px 8px;display:none;font-weight:500}
      .iaw-typing-dots{display:inline-flex;gap:3px;margin-left:6px;vertical-align:middle}
      .iaw-typing-dots span{width:5px;height:5px;border-radius:50%;background:var(--iaw-muted);display:inline-block;animation:iaw-dots 1.2s infinite ease-in-out;font-size:0;line-height:0}
      .iaw-typing-dots span:nth-child(2){animation-delay:.15s}
      .iaw-typing-dots span:nth-child(3){animation-delay:.3s}
      @keyframes iaw-dots{0%,80%,100%{opacity:.3;transform:translateY(0)}40%{opacity:1;transform:translateY(-3px)}}

      .iaw-media img{max-width:240px;border-radius:14px;border:1px solid var(--iaw-line);display:block}
      .iaw-media video{max-width:280px;border-radius:14px}
      .iaw-media-audio{background:#fff;border-radius:14px;padding:6px 8px;border:1px solid rgba(15,23,42,.08)}
      .iaw-media-audio audio{display:block;width:240px;max-width:100%;height:36px}
      .iaw-me .iaw-media-audio{background:rgba(255,255,255,.95)}
      .iaw-bubble-audio{padding:8px 10px}
      .iaw-row-failed{opacity:.6}
      .iaw-record{color:#ef4444;margin-left:6px;font-size:12px;font-weight:700;letter-spacing:.02em}

      .iaw-preform{flex:1;min-height:0;padding:24px 22px;background:var(--iaw-surface);display:none;flex-direction:column}
      .iaw-preform-title{font-size:16px;font-weight:700;color:var(--iaw-text);margin-bottom:6px;letter-spacing:-.01em}
      .iaw-preform-subtitle{font-size:13px;color:var(--iaw-muted);margin-bottom:18px;line-height:1.45}
      .iaw-preform-fields{flex:1;min-height:0;display:flex;flex-direction:column;gap:10px}
      .iaw-preform-fields input,.iaw-preform-fields textarea{padding:13px 14px;border:1px solid var(--iaw-line);border-radius:12px;outline:none;font:inherit;color:var(--iaw-text);background:var(--iaw-bg);transition:border-color .2s ease,box-shadow .2s ease,background .2s ease}
      .iaw-preform-fields textarea{flex:1;min-height:96px;line-height:1.42;resize:none}
      .iaw-preform-fields input::placeholder,.iaw-preform-fields textarea::placeholder{color:var(--iaw-muted)}
      .iaw-preform-fields input:focus,.iaw-preform-fields textarea:focus{border-color:var(--iaw-accent);background:var(--iaw-surface);box-shadow:0 0 0 3px rgba(247,148,29,.14)}
      .iaw-preform-actions{display:flex;justify-content:flex-end;margin-top:16px}
      .iaw-preform-btn{border:none;border-radius:12px;padding:12px 20px;cursor:pointer;background:linear-gradient(135deg,var(--iaw-accent),var(--iaw-accent-strong));color:#fff;font:inherit;font-weight:700;letter-spacing:.01em;box-shadow:0 12px 24px -10px rgba(247,148,29,.6);transition:transform .2s ease,box-shadow .2s ease}
      .iaw-preform-btn:hover{transform:translateY(-1px);box-shadow:0 16px 30px -10px rgba(247,148,29,.65)}
      .iaw-preform-btn:disabled{opacity:.55;cursor:not-allowed;transform:none;box-shadow:none}

      .iaw-footer{flex-shrink:0;padding:8px 12px calc(8px + env(safe-area-inset-bottom,0px));font-size:11px;color:var(--iaw-muted);text-align:center;border-top:1px solid var(--iaw-line);background:var(--iaw-surface);letter-spacing:.01em}
      .iaw-footer strong{color:var(--iaw-text);font-weight:700}
      .iaw-footer a{color:inherit}
      .iaw-session-closed{display:none;flex-shrink:0;align-items:center;justify-content:space-between;gap:8px;padding:8px 10px;background:#fef3c7;border-top:1px solid #fcd34d;font-size:12px;color:#92400e}
      .iaw-session-closed-btn{border:none;border-radius:6px;padding:6px 10px;cursor:pointer;background:linear-gradient(135deg,var(--iaw-accent),var(--iaw-accent-strong));color:#fff;font-size:12px;font-family:inherit}

      @keyframes iaw-bounce-anim{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
      @keyframes iaw-shake-anim{0%,100%{transform:translateX(0)}25%{transform:translateX(-5px)}75%{transform:translateX(5px)}}
      @keyframes iaw-pulse-anim{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}
      .iaw-bounce{animation:iaw-bounce-anim .5s ease 3}
      .iaw-shake{animation:iaw-shake-anim .3s ease 3}
      .iaw-pulse{animation:iaw-pulse-anim .6s ease 3}

      .iaw-reactions{display:flex;flex-wrap:wrap;gap:4px;margin-top:8px}
      .iaw-reaction-badge{background:rgba(15,23,42,.06);border:1px solid var(--iaw-line);border-radius:999px;padding:2px 8px;font-size:12px;color:var(--iaw-text);cursor:default}
      .iaw-me .iaw-reaction-badge{background:rgba(255,255,255,.22);color:#fff;border-color:rgba(255,255,255,.32)}
      .iaw-reaction-picker{display:none;position:absolute;bottom:100%;left:0;background:#fff;border:1px solid var(--iaw-line);border-radius:999px;box-shadow:0 14px 28px -8px rgba(15,23,42,.2);padding:4px;gap:2px;z-index:10}
      .iaw-reaction-picker button{border:none;background:transparent;font-size:18px;cursor:pointer;padding:5px;border-radius:999px;transition:background .15s,transform .15s}
      .iaw-reaction-picker button:hover{background:rgba(15,23,42,.06);transform:scale(1.12)}

      .iaw-quoted{border-left:3px solid var(--iaw-accent);background:rgba(15,23,42,.05);padding:8px 10px;margin-bottom:6px;border-radius:8px;font-size:12px;max-height:60px;overflow:hidden}
      .iaw-quoted-sender{display:block;font-size:11px;margin-bottom:2px;color:var(--iaw-accent);font-weight:700}
      .iaw-me .iaw-quoted{background:rgba(255,255,255,.18);border-left-color:rgba(255,255,255,.55)}
      .iaw-me .iaw-quoted-sender{color:rgba(255,255,255,.85)}

      .iaw-reply-bar{display:none;align-items:center;gap:10px;padding:10px 14px;border-top:1px solid var(--iaw-line);background:rgba(247,148,29,.08);font-size:12px}
      .iaw-reply-bar-content{flex:1;min-width:0;overflow:hidden;border-left:3px solid var(--iaw-accent);padding-left:8px}
      .iaw-reply-bar-sender{display:block;font-size:11px;color:var(--iaw-accent);font-weight:700}
      .iaw-reply-bar-text{display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--iaw-muted)}
      .iaw-reply-bar-close{border:none;background:transparent;font-size:16px;cursor:pointer;color:var(--iaw-muted);padding:4px 6px;border-radius:8px}
      .iaw-reply-bar-close:hover{background:rgba(15,23,42,.06);color:var(--iaw-text)}

      .iaw-reply-btn{display:none;border:none;background:rgba(15,23,42,.06);font-size:13px;cursor:pointer;color:var(--iaw-muted);padding:4px 7px;align-self:center;border-radius:8px;margin:0 4px}
      .iaw-reply-btn:hover{background:rgba(15,23,42,.12);color:var(--iaw-text)}
      .iaw-row:hover .iaw-reply-btn{display:inline-flex}
      .iaw-me .iaw-reply-btn{order:-1}

      .iaw-status{margin-left:6px;font-size:11px}
      .iaw-tick-pending{color:rgba(255,255,255,.6)}
      .iaw-tick-sent{color:rgba(255,255,255,.7)}
      .iaw-tick-delivered{color:rgba(255,255,255,.85)}
      .iaw-tick-read{color:#53bdeb}

      .iaw-location-card{padding:12px;border-radius:14px;background:linear-gradient(135deg,#ecfdf3,#d4f4dd);border:1px solid #b9eccc}
      .iaw-location-card a{color:#146c43;text-decoration:none;display:block;font-weight:600}
      .iaw-location-card small{color:#2f7c54;font-weight:500}
      .iaw-contact-card{padding:12px;border-radius:14px;background:linear-gradient(135deg,#eff6ff,#dbeafe);border:1px solid #bfdbfe;display:flex;flex-direction:column;gap:4px}
      .iaw-contact-card a{color:#1d4ed8;text-decoration:none;font-weight:600}
      .iaw-doc-card{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:14px;background:linear-gradient(135deg,#fffbeb,#fef3c7);border:1px solid #fde68a}
      .iaw-doc-download{color:#b45309;font-size:12px;text-decoration:none;white-space:nowrap;font-weight:700}
      .iaw-transcription{font-size:12px;color:var(--iaw-muted);margin-top:6px;font-style:italic}

      .iaw-interactive-header{margin-bottom:6px;font-size:14px}
      .iaw-interactive-footer{margin-top:6px;color:var(--iaw-muted)}
      .iaw-interactive-actions{display:flex;flex-direction:column;gap:8px;margin-top:10px}
      .iaw-interactive-btn,.iaw-interactive-list-btn,.iaw-interactive-cta,.iaw-interactive-flow-btn{
        display:block;width:100%;padding:10px 14px;border:1px solid var(--iaw-line);border-radius:12px;
        background:#fff;color:var(--iaw-text);font-size:14px;font-family:inherit;cursor:pointer;
        text-align:center;text-decoration:none;transition:background .15s ease,border-color .15s ease
      }
      .iaw-interactive-btn:hover,.iaw-interactive-list-btn:hover,.iaw-interactive-flow-btn:hover{
        background:rgba(247,148,29,.08);border-color:rgba(247,148,29,.45)
      }
      .iaw-interactive-cta{background:linear-gradient(135deg,var(--iaw-accent),var(--iaw-accent-strong));color:#fff;border:none}
      .iaw-interactive-list-menu{display:none;flex-direction:column;gap:4px;margin-top:4px;padding:8px;border:1px solid var(--iaw-line);border-radius:12px;background:#fff;max-height:220px;overflow-y:auto}
      .iaw-interactive-list-menu.iaw-show{display:flex}
      .iaw-interactive-list-sec{font-size:11px;font-weight:700;color:var(--iaw-muted);padding:6px 8px 2px;text-transform:uppercase;letter-spacing:.04em}
      .iaw-interactive-list-row{padding:10px 12px;border-radius:10px;cursor:pointer;border:1px solid transparent}
      .iaw-interactive-list-row:hover{background:rgba(247,148,29,.08);border-color:rgba(247,148,29,.25)}
      .iaw-interactive-list-row-title{font-weight:600;font-size:14px}
      .iaw-interactive-list-row-desc{font-size:12px;color:var(--iaw-muted);margin-top:2px}
      .iaw-me .iaw-interactive-btn,.iaw-me .iaw-interactive-list-btn,.iaw-me .iaw-interactive-flow-btn{background:rgba(255,255,255,.12);border-color:rgba(255,255,255,.28);color:#fff}
      .iaw-me .iaw-interactive-list-menu{background:rgba(255,255,255,.1);border-color:rgba(255,255,255,.25)}

      @media (max-width:600px){
        .iaw-launcher{padding:11px 16px}
        .iaw-panel{
          inset:0;width:100%;height:100%;height:100dvh;max-width:100%;max-height:none;
          border-radius:0;border:0;
          animation:iaw-panel-mobile-in .26s ease both;
        }
        @keyframes iaw-panel-mobile-in{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}}
        .iaw-header{padding:max(18px,env(safe-area-inset-top)) 18px 16px}
        .iaw-input{padding:10px 12px}
        .iaw-input input,.iaw-preform-fields input,.iaw-preform-fields textarea{font-size:max(16px,1em)}
        .iaw-bubble{max-width:88%}
        .iaw-preform{padding:22px 18px}
      }

      @media (prefers-reduced-motion:reduce){
        .iaw-launcher,.iaw-panel,.iaw-bubble,.iaw-btn,.iaw-close,.iaw-preform-btn,.iaw-launcher-pulse{animation:none !important;transition:none !important}
      }
    `;

    const styleEl = document.createElement("style");
    styleEl.textContent = customCss ? `${styles}\n${customCss}` : styles;
    document.head.appendChild(styleEl);

    const launcher = document.createElement("button");
    launcher.type = "button";
    launcher.className = `iaw-launcher ${customClass}`.trim();
    launcher.setAttribute("aria-label", t("launcher", "Abrir chat"));
    launcher.style[position.includes("right") ? "right" : "left"] = "20px";
    launcher.style[position.includes("bottom") ? "bottom" : "top"] = "20px";
    launcher.innerHTML = `
      <span class="iaw-launcher-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
        </svg>
      </span>
      <span class="iaw-launcher-label">${t("launcher", "Chat")}</span>
      <span class="iaw-launcher-pulse" aria-hidden="true"></span>
    `;
    launcherRef = launcher;

    const panel = document.createElement("div");
    panel.className = [
      "iaw-panel",
      fullScreen ? "iaw-fullscreen" : "",
      hideClose ? "iaw-hide-close" : "",
      customClass,
    ].filter(Boolean).join(" ");
    panel.style[position.includes("right") ? "right" : "left"] = "20px";
    panel.style[position.includes("bottom") ? "bottom" : "top"] = "70px";
    panelRef = panel;

    const header = document.createElement("div");
    header.className = "iaw-header";
    const headerTitle = hasCustomTitle ? title : t("header_title", title);
    const headerSubtitle = t("header_subtitle", "Online agora");
    const headerInitial = (headerTitle || "AI").trim().charAt(0).toUpperCase() || "A";
    header.innerHTML = `
      <div class="iaw-header-info">
        <div class="iaw-avatar" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2a4 4 0 0 1 4 4v1h1a3 3 0 0 1 3 3v3a3 3 0 0 1-3 3h-1v1a4 4 0 0 1-4 4 4 4 0 0 1-4-4v-1H7a3 3 0 0 1-3-3v-3a3 3 0 0 1 3-3h1V6a4 4 0 0 1 4-4z"/>
            <circle cx="9.5" cy="11" r="1" fill="currentColor"/>
            <circle cx="14.5" cy="11" r="1" fill="currentColor"/>
            <path d="M9 14.5c.8.7 1.8 1 3 1s2.2-.3 3-1"/>
          </svg>
        </div>
        <div class="iaw-header-text">
          <strong>${htmlEscapeAttr(headerTitle)}</strong>
          <span><span class="iaw-online-dot" aria-hidden="true"></span>${htmlEscapeAttr(headerSubtitle)}</span>
        </div>
      </div>
      <button type="button" class="iaw-close" aria-label="${htmlEscapeAttr(t("close", "Fechar"))}" title="${htmlEscapeAttr(t("close", "Fechar"))}">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    `;

    const body = document.createElement("div");
    body.className = "iaw-body";

    const messages = document.createElement("div");
    messages.className = "iaw-messages";

    const typing = document.createElement("div");
    typing.className = "iaw-typing";
    typing.style.display = "none";
    typing.textContent = t("typing", "Agente digitando…");

    const inputWrap = document.createElement("div");
    inputWrap.className = "iaw-input";

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.accept = "image/*,audio/*,application/pdf";
    fileInput.style.display = "none";

    const emojiPanel = document.createElement("div");
    emojiPanel.className = "iaw-emoji";
    const emojis = [
      "😀",
      "😁",
      "😂",
      "😊",
      "😍",
      "😎",
      "😢",
      "🙏",
      "👍",
      "👎",
      "🔥",
      "🎉",
      "💡",
      "💬",
      "📎",
      "📷",
      "🎥",
      "🎧",
    ];
    emojis.forEach((e) => {
      const b = document.createElement("button");
      b.textContent = e;
      b.onclick = () => {
        input.value += e;
        input.focus();
      };
      emojiPanel.appendChild(b);
    });

    const input = document.createElement("input");
    input.placeholder = placeholderText || t("placeholder", "Digite sua mensagem…");

    const toolbar = document.createElement("div");
    toolbar.className = "iaw-toolbar";

    const SVG_EMOJI = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>';
    const SVG_ATTACH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.48-8.48l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>';
    const SVG_MIC = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>';
    const SVG_LIVE = '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><rect x="4" y="10" width="2.5" height="4" rx="1"/><rect x="8.5" y="6" width="2.5" height="12" rx="1"/><rect x="13" y="8" width="2.5" height="8" rx="1"/><rect x="17.5" y="4" width="2.5" height="16" rx="1"/></svg>';
    const SVG_SEND = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>';

    const btnEmoji = document.createElement("button");
    btnEmoji.type = "button";
    btnEmoji.className = "iaw-btn";
    btnEmoji.title = t("emoji", "Emojis");
    btnEmoji.setAttribute("aria-label", t("emoji", "Emojis"));
    btnEmoji.innerHTML = SVG_EMOJI;

    const btnAttach = document.createElement("button");
    btnAttach.type = "button";
    btnAttach.className = "iaw-btn";
    btnAttach.title = t("attach", "Anexar");
    btnAttach.setAttribute("aria-label", t("attach", "Anexar"));
    btnAttach.innerHTML = SVG_ATTACH;

    const btnMic = document.createElement("button");
    btnMic.type = "button";
    btnMic.className = "iaw-btn";
    btnMic.title = t("record", "Gravar áudio");
    btnMic.setAttribute("aria-label", t("record", "Gravar áudio"));
    btnMic.innerHTML = SVG_MIC;

    const btnLive = document.createElement("button");
    btnLive.type = "button";
    btnLive.className = "iaw-btn iaw-btn-live";
    btnLive.title = t("live_start", "Conversar por voz (Live)");
    btnLive.setAttribute("aria-label", t("live_start", "Conversar por voz (Live)"));
    btnLive.innerHTML = SVG_LIVE;
    btnLive.style.display = allowLiveVoice ? "" : "none";

    const btnSend = document.createElement("button");
    btnSend.type = "button";
    btnSend.className = "iaw-btn primary";
    btnSend.title = t("send", "Enviar");
    btnSend.setAttribute("aria-label", t("send", "Enviar"));
    btnSend.innerHTML = SVG_SEND;

    toolbar.appendChild(btnEmoji);
    if (allowMedia) toolbar.appendChild(btnAttach);
    if (allowAudio) toolbar.appendChild(btnMic);
    if (allowLiveVoice) toolbar.appendChild(btnLive);
    toolbar.appendChild(btnSend);

    inputWrap.appendChild(emojiPanel);
    inputWrap.appendChild(fileInput);
    inputWrap.appendChild(input);
    inputWrap.appendChild(toolbar);

    const replyBar = document.createElement("div");
    replyBar.className = "iaw-reply-bar";
    replyBar.style.display = "none";
    replyBar.innerHTML = '<div class="iaw-reply-bar-content"><b class="iaw-reply-bar-sender"></b><span class="iaw-reply-bar-text"></span></div><button class="iaw-reply-bar-close">✕</button>';
    replyBar.querySelector(".iaw-reply-bar-close").addEventListener("click", () => setReplyingTo(null));

    const footer = document.createElement("div");
    footer.className = "iaw-footer";

    const preform = document.createElement("div");
    preform.className = "iaw-preform";
    preform.style.display = "none";

    const preformTitle = document.createElement("div");
    preformTitle.className = "iaw-preform-title";
    preformTitle.textContent = t(
      "preform_title",
      "Antes de iniciar, confirme seus dados:",
    );
    preform.appendChild(preformTitle);

    const preformFields = document.createElement("div");
    preformFields.className = "iaw-preform-fields";

    const preformNameInput = document.createElement("input");
    preformNameInput.type = "text";
    preformNameInput.placeholder = t("preform_name", "Seu nome");
    preformNameInput.style.display = collectName ? "block" : "none";

    const preformPhoneInput = document.createElement("input");
    preformPhoneInput.type = "text";
    preformPhoneInput.placeholder = t("preform_phone", "Seu telefone");
    preformPhoneInput.style.display = collectPhone ? "block" : "none";

    const preformEmailInput = document.createElement("input");
    preformEmailInput.type = "email";
    preformEmailInput.placeholder = t("preform_email", "Seu e-mail");
    preformEmailInput.style.display = collectEmail ? "block" : "none";

    const preformMessageInput = document.createElement("textarea");
    preformMessageInput.rows = 2;
    preformMessageInput.placeholder =
      theme.preformMessagePlaceholder || t("preform_message", "Sua mensagem...");
    preformMessageInput.value = configuredDefaultMessage || "";

    const customFieldInputs = customFields.map((cf) => {
      const inp = document.createElement("input");
      inp.type = "text";
      inp.placeholder = cf.placeholder || cf.label || "";
      inp.dataset.cfKey = cf.key;
      if (cf.required) inp.required = true;
      return inp;
    });

    preformFields.appendChild(preformNameInput);
    preformFields.appendChild(preformPhoneInput);
    preformFields.appendChild(preformEmailInput);
    customFieldInputs.forEach((inp) => preformFields.appendChild(inp));
    preformFields.appendChild(preformMessageInput);
    preform.appendChild(preformFields);

    const preformActions = document.createElement("div");
    preformActions.className = "iaw-preform-actions";
    const preformSubmitBtn = document.createElement("button");
    preformSubmitBtn.className = "iaw-preform-btn";
    preformSubmitBtn.textContent = t("preform_continue", "Continuar");
    preformActions.appendChild(preformSubmitBtn);
    preform.appendChild(preformActions);

    const sessionClosedBar = document.createElement("div");
    sessionClosedBar.className = "iaw-session-closed";
    sessionClosedBar.style.display = "none";
    const sessionClosedMsg = document.createElement("span");
    sessionClosedMsg.className = "iaw-session-closed-msg";
    const sessionClosedNewBtn = document.createElement("button");
    sessionClosedNewBtn.type = "button";
    sessionClosedNewBtn.className = "iaw-session-closed-btn";
    sessionClosedBar.appendChild(sessionClosedMsg);
    sessionClosedBar.appendChild(sessionClosedNewBtn);

    body.appendChild(preform);
    body.appendChild(messages);
    body.appendChild(typing);
    body.appendChild(replyBar);
    body.appendChild(sessionClosedBar);
    body.appendChild(inputWrap);
    body.appendChild(footer);
    panel.appendChild(header);
    panel.appendChild(body);

    document.body.appendChild(launcher);
    document.body.appendChild(panel);
    if (openOnLoad || urlInitial.open || urlInitial.message) {
      panel.style.display = "flex";
    }

    // Session state (restored after storage helpers are declared)
    let session = null;
    let mediaRecorder = null;
    let recording = false;
    let chunks = [];
    const STORAGE_KEY = `iaw_session_v2_${encodeURIComponent(
      getApiBase(),
    )}_${publicKey}`;
    const PROFILE_STORAGE_KEY = `iaw_profile_v1_${encodeURIComponent(
      getApiBase(),
    )}_${publicKey}`;
    const ANON_PHONE_KEY = `iaw_anon_phone_v1_${encodeURIComponent(
      getApiBase(),
    )}_${publicKey}`;
    const VISITOR_TOKEN_KEY = `iaw_visitor_token_v1_${encodeURIComponent(
      getApiBase(),
    )}_${publicKey}`;
    let knownMessageIds = new Set();
    let widgetMessageCache = [];
    const pendingOptimisticMessages = new Map();
    let hasMoreWidgetHistory = true;
    let loadingOlderWidget = false;
    let pollInterval = null;
    let pollingInFlight = false;
    let fastPollUntil = 0;
    let lastWidgetActivityAt = Date.now();
    let wsConn = null;
    let welcomeMessageShown = false;
    function getSortedWidgetMessages(arr) {
      return (arr || [])
        .slice()
        .sort(
          (a, b) =>
            parseWidgetTimestampMs(a.timestamp || 0) -
            parseWidgetTimestampMs(b.timestamp || 0),
        );
    }

    function normalizeWidgetTimestamp(value) {
      if (value instanceof Date) return value.toISOString();
      if (typeof value !== "string") return value;
      const trimmed = value.trim();
      if (!trimmed) return trimmed;
      return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed) ? trimmed : `${trimmed}Z`;
    }

    function parseWidgetTimestampMs(value) {
      return new Date(normalizeWidgetTimestamp(value)).getTime();
    }

    function getOrCreateVisitorToken() {
      try {
        const existing = (localStorage.getItem(VISITOR_TOKEN_KEY) || "").trim();
        if (existing.length >= 32) return existing;
      } catch (_) {}
      let body = "";
      try {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
          body = crypto.randomUUID().replace(/-/g, "");
        }
      } catch (_) {}
      if (body.length < 32) {
        body = Array.from({ length: 32 }, () =>
          Math.floor(Math.random() * 16).toString(16),
        ).join("");
      }
      const token = `wv_${body.slice(0, 32)}`;
      try {
        localStorage.setItem(VISITOR_TOKEN_KEY, token);
      } catch (_) {}
      return token;
    }

    function sessionVisitorToken() {
      return (session && session.visitor_token) || "";
    }

    function persistSession() {
      if (session && session.id && session.visitor_token)
        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
              id: session.id,
              visitor_token: session.visitor_token,
            }),
          );
        } catch (_) {}
    }
    function loadPersistedSession() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const data = JSON.parse(raw);
          if (data && data.id && data.visitor_token) {
            return { id: data.id, visitor_token: data.visitor_token };
          }
        }
      } catch (_) {}
      return null;
    }
    session = loadPersistedSession();

    const dataAttrsContext = {};
    try {
      for (const attr of Array.from(scriptEl.attributes || [])) {
        const name = attr?.name || "";
        if (!name.startsWith("data-user-")) continue;
        const key = name.slice("data-user-".length);
        if (!key || ["name", "phone", "email", "context"].includes(key)) continue;
        dataAttrsContext[key] = attr.value;
      }
    } catch (_) {}

    function loadPersistedProfile() {
      try {
        const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
        if (!raw) return {};
        const data = JSON.parse(raw);
        return data && typeof data === "object" ? data : {};
      } catch (_) {
        return {};
      }
    }

    function persistProfile(profile) {
      try {
        localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile || {}));
      } catch (_) {}
    }

    function getOrCreateAnonPhone() {
      try {
        const existing = (localStorage.getItem(ANON_PHONE_KEY) || "").trim();
        if (existing) return existing;
        const generated =
          "widget-visitor-" +
          Math.random().toString(36).slice(2, 10) +
          Date.now().toString(36);
        localStorage.setItem(ANON_PHONE_KEY, generated);
        return generated;
      } catch (_) {
        return "widget-visitor-" + Date.now().toString(36);
      }
    }

    function getVisitorProfile() {
      const saved = loadPersistedProfile();
      const merged = {
        name: urlInitial.profile.name || presetName || saved.name || "",
        phone: urlInitial.profile.phone || presetPhone || saved.phone || "",
        email: urlInitial.profile.email || presetEmail || saved.email || "",
        context: {
          ...(saved.context || {}),
          ...presetContext,
          ...dataAttrsContext,
          ...(urlInitial.context || {}),
        },
      };
      return merged;
    }

    function requiresPreform(profile) {
      if (bypassPreform) return false;
      if (collectName && !String(profile.name || "").trim()) return true;
      if (collectPhone && !String(profile.phone || "").trim()) return true;
      if (collectEmail && !String(profile.email || "").trim()) return true;
      return false;
    }

    function syncPreformVisibility(profile) {
      const needs = requiresPreform(profile);
      preform.style.display = needs ? "flex" : "none";
      messages.style.display = needs ? "none" : "block";
      inputWrap.style.display = needs ? "none" : "flex";
      typing.style.display = needs ? "none" : typing.style.display;
      if (!needs) return;
      preformNameInput.value = profile.name || "";
      preformPhoneInput.value = profile.phone || "";
      preformEmailInput.value = profile.email || "";
      if (!preformMessageInput.value && configuredDefaultMessage) {
        preformMessageInput.value = configuredDefaultMessage;
      }
    }

    function scrollToBottom() {
      messages.scrollTop = messages.scrollHeight;
    }
    function htmlEscape(text) {
      return (text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    }

    /** Remove tags do protocolo do agente (<text>, <voice>, tool_result, JSON {"tool":...}) antes do htmlEscape. */
    function stripAgentTags(text) {
      if (!text) return "";
      let s = String(text);
      s = s.replace(/<tool_result>[\s\S]*?<\/tool_result>/gi, " ");
      s = s.replace(/<\/?(?:text|voice|spoken)(?:\s+[^>]*)?>/gi, "");
      s = s.replace(/<interactive[\s\S]*?<\/interactive>/gi, " ");
      s = s.replace(/<interactive_reply[^>]*\/?>/gi, " ");
      s = s.replace(/<silence_ms[\s\S]*?<\/silence_ms>/gi, "");
      let i = 0;
      while (i < s.length) {
        const start = s.indexOf("{", i);
        if (start === -1) break;
        let depth = 0;
        let j = start;
        for (; j < s.length; j++) {
          const ch = s[j];
          if (ch === "{") depth++;
          else if (ch === "}") {
            depth--;
            if (depth === 0) break;
          }
        }
        if (depth !== 0) break;
        const jsonStr = s.slice(start, j + 1);
        try {
          const obj = JSON.parse(jsonStr);
          if (obj && typeof obj === "object" && obj.tool) {
            s = s.slice(0, start) + s.slice(j + 1);
            i = start;
            continue;
          }
        } catch (_) {
             /* ignore */
        }
        i = start + 1;
      }
      return s.replace(/\s+/g, " ").trim();
    }
    function htmlEscapeAttr(text) {
      return String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }
    function linkify(text) {
      const urlRe = /(https?:\/\/[^\s]+)/g;
      return text.replace(
        urlRe,
        '<a href="$1" target="_blank" rel="noopener">$1</a>',
      );
    }
    function formatWhatsApp(text) {
      let t = text;
      const imgPlaceholders = [];
      t = t.replace(/!\[([^\]]*)\]\((\S+?)(?:\s+"[^"]*")?\)/g, (_m, alt, url) => {
        const ph = `__IMG_PH_${imgPlaceholders.length}__`;
        imgPlaceholders.push(`<div class="iaw-media"><img src="${htmlEscape(url)}" alt="${htmlEscape(alt)}" style="max-width:220px;border-radius:8px;border:1px solid #e5e7eb"/></div>`);
        return ph;
      });
      t = htmlEscape(t);
      t = t.replace(/\*([^*]+)\*/g, "<b>$1</b>");
      t = t.replace(/_([^_]+)_/g, "<i>$1</i>");
      t = t.replace(/~([^~]+)~/g, "<s>$1</s>");
      t = linkify(t);
      t = t.replace(/\n/g, "<br>");
      imgPlaceholders.forEach((html, i) => {
        t = t.replace(`__IMG_PH_${i}__`, html);
      });
      return t;
    }

    sessionClosedMsg.textContent = t("session_closed", "Sessão encerrada");
    sessionClosedNewBtn.textContent = t("new_conversation", "Nova conversa");
    if (showBranding) {
      const footerYear = String(new Date().getFullYear());
      const footerRaw = (t("footer", "© {{year}} InovationAI") || "").replace(
        "{{year}}",
        footerYear,
      );
      footer.innerHTML = `<a href="https://inovationai.ai" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:underline">${htmlEscape(footerRaw)}</a>`;
    } else {
      footer.style.display = "none";
    }

    let sessionEnded = false;

    function isClosedSessionStatus(st) {
      const s = String(st || "").toLowerCase();
      return s === "finished" || s === "closed" || s === "ended";
    }

    function applySessionClosedState(st) {
      const closed = isClosedSessionStatus(st);
      sessionEnded = closed;
      sessionClosedBar.style.display = closed ? "flex" : "none";
      input.disabled = closed;
      btnSend.disabled = closed;
      btnAttach.disabled = closed;
      btnMic.disabled = closed;
      btnLive.disabled = closed;
      fileInput.disabled = closed;
    }

    let liveCallHandle = null;
    let liveChunkPromise = null;

    function loadLiveChunk() {
      if (window.InovationAgentsWidgetLive?.startWidgetLiveCall) {
        return Promise.resolve(window.InovationAgentsWidgetLive);
      }
      if (liveChunkPromise) return liveChunkPromise;
      liveChunkPromise = new Promise((resolve, reject) => {
        const baseSrc = (scriptEl.src || "").split("?")[0];
        const liveSrc = baseSrc.replace(
          /agents-widget(\.min)?\.js$/i,
          "agents-widget-live.min.js",
        );
        if (!liveSrc || liveSrc === baseSrc) {
          reject(new Error("live_chunk_url_unresolved"));
          return;
        }
        const s = document.createElement("script");
        s.src = liveSrc;
        s.async = true;
        s.onload = () => {
          if (window.InovationAgentsWidgetLive?.startWidgetLiveCall) {
            resolve(window.InovationAgentsWidgetLive);
          } else {
            reject(new Error("live_chunk_missing_export"));
          }
        };
        s.onerror = () => reject(new Error("live_chunk_load_failed"));
        document.head.appendChild(s);
      });
      return liveChunkPromise;
    }

    async function startLiveVoice() {
      if (liveCallHandle || sessionEnded) return;
      if (recording) {
        try {
          stopRecording();
        } catch (_) {}
      }
      if (!session || !session.id) {
        try {
          await ensureSession();
        } catch (_) {
          appendBubble(
            t("connection_failed", "Falha de conexão. Tente novamente."),
            "system",
          );
          return;
        }
      }
      const visitorToken = sessionVisitorToken();
      if (!session?.id || !visitorToken) {
        appendBubble(
          t("live_session_required", "Inicie o chat antes da chamada Live."),
          "system",
        );
        return;
      }
      btnLive.classList.add("is-active");
      btnLive.disabled = true;
      btnMic.disabled = true;
      input.disabled = true;
      btnSend.disabled = true;
      try {
        const liveApi = await loadLiveChunk();
        liveCallHandle = await liveApi.startWidgetLiveCall({
          apiBase: getApiBase(),
          publicKey,
          visitorToken,
          sessionId: session.id,
          panelEl: panel,
          t,
          onTurn: () => {
            try {
              loadHistory();
            } catch (_) {}
          },
          onEnded: () => {
            liveCallHandle = null;
            btnLive.classList.remove("is-active");
            btnLive.disabled = sessionEnded;
            btnMic.disabled = sessionEnded;
            input.disabled = sessionEnded;
            btnSend.disabled = sessionEnded;
            try {
              loadHistory();
            } catch (_) {}
          },
          onError: (msg) => {
            if (msg) {
              appendBubble(String(msg), "system");
            }
          },
        });
      } catch (err) {
        console.error("[WidgetLive] failed to start", err);
        liveCallHandle = null;
        btnLive.classList.remove("is-active");
        btnLive.disabled = sessionEnded;
        btnMic.disabled = sessionEnded;
        input.disabled = sessionEnded;
        btnSend.disabled = sessionEnded;
        appendBubble(
          t("live_error", "Não foi possível iniciar a chamada Live."),
          "system",
        );
      }
    }

    function resetWidgetSession() {
      session = null;
      sessionEnded = false;
      knownMessageIds = new Set();
      pendingOptimisticMessages.clear();
      widgetMessageCache = [];
      hasMoreWidgetHistory = true;
      loadingOlderWidget = false;
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (_) {}
      stopPolling();
      messages.innerHTML = "";
      applySessionClosedState("");
      syncPreformVisibility(getVisitorProfile());
    }

    sessionClosedNewBtn.addEventListener("click", () => {
      resetWidgetSession();
    });

    async function ensureSession(profileOverride = null) {
      if (session) return session;
      const restored = loadPersistedSession();
      if (restored) {
        session = restored;
        persistSession();
        return session;
      }
      const profile = profileOverride || getVisitorProfile();
      const cfValues = profile._customFields || {};
      const preformMsg = profile._preformMessage || "";
      const finalProfile = {
        name: String(profile.name || "").trim() || "Visitante",
        phone: String(profile.phone || "").trim() || getOrCreateAnonPhone(),
        email: String(profile.email || "").trim(),
        context: profile.context || {},
      };
      const visitorToken = getOrCreateVisitorToken();
      const payload = {
        public_key: publicKey,
        visitor_token: visitorToken,
        client_phone: finalProfile.phone,
        client_name: finalProfile.name,
        client_email: finalProfile.email || null,
        user_context: {
          ...finalProfile.context,
          ...cfValues,
          preform_bypassed: bypassPreform,
          preform_required: {
            name: collectName,
            phone: collectPhone,
            email: collectEmail,
          },
          custom_fields: cfValues,
        },
      };
      const rtSession = await getRecaptchaToken("widget_session");
      if (rtSession) payload.recaptcha_token = rtSession;
      const created = await createWidgetSession(payload);
      const issuedToken =
        created.widget_visitor_token || visitorToken || getOrCreateVisitorToken();
      try {
        localStorage.setItem(VISITOR_TOKEN_KEY, issuedToken);
      } catch (_) {}
      session = { id: created.id, visitor_token: issuedToken };
      persistProfile(finalProfile);
      persistSession();

      let introText = introMessageTemplate
        .replace(/\{name\}/g, finalProfile.name)
        .replace(/\{phone\}/g,
          finalProfile.phone && !/^widget-visitor-/.test(finalProfile.phone)
            ? finalProfile.phone : "")
        .replace(/\{email\}/g, finalProfile.email || "")
        .replace(/\{message\}/g, preformMsg);

      for (const cf of customFields) {
        introText = introText.replace(
          new RegExp("\\{" + cf.key + "\\}", "g"),
          cfValues[cf.key] || "",
        );
      }

      const cfSummaryLines = customFields
        .filter((cf) => cfValues[cf.key])
        .map((cf) => `${cf.label}: ${cfValues[cf.key]}`);
      if (cfSummaryLines.length > 0) {
        introText = introText.trimEnd() + "\n" + cfSummaryLines.join("\n");
      }

      introText = introText
        .split("\n")
        .filter((line) => !/:\s*$/.test(line.trim()))
        .join("\n")
        .trim();
      try {
        const introPayload = {
          session_id: session.id,
          visitor_token: sessionVisitorToken(),
          content: introText,
          public_key: publicKey,
        };
        const rtIntro = await getRecaptchaToken("widget_message");
        if (rtIntro) introPayload.recaptcha_token = rtIntro;
        const introResp = await sendWidgetMessage(introPayload);
        if (introResp?.message) appendMessageFromApi(introResp.message);
        if (introResp?.agent_reply) appendMessageFromApi(introResp.agent_reply);
      } catch (_) {}

      return session;
    }

    async function ensureSessionReady() {
      if (session) return session;
      const profile = getVisitorProfile();
      syncPreformVisibility(profile);
      if (requiresPreform(profile)) return null;
      return ensureSession(profile);
    }

    const absMedia = (url) =>
      toAbsoluteMediaUrl(url || "", {
        sessionId: session?.id,
        publicKey,
      });

    let expectingAgentReply = false;
    let typingHideTimeout = null;
    let typingTimeoutRef = null;

    function setTyping(v) {
      const el = document.querySelector(".iaw-typing");
      if (!el) return;
      if (v && !expectingAgentReply) return;
      el.style.display = v ? "block" : "none";
      if (v) {
        scrollToBottom();
      } else {
        clearTimeout(typingHideTimeout);
        typingHideTimeout = null;
      }
    }

    function hideTyping() {
      expectingAgentReply = false;
      const el = document.querySelector(".iaw-typing");
      if (el) el.style.display = "none";
      clearTimeout(typingHideTimeout);
      typingHideTimeout = null;
      clearTimeout(typingTimeoutRef);
      typingTimeoutRef = null;
    }

    function appendBubble(html, who = "agent", isMedia = false) {
      const row = document.createElement("div");
      row.className = `iaw-row iaw-${who}`;
      const b = document.createElement("div");
      b.className = `iaw-bubble ${isMedia ? "iaw-media" : ""}`.trim();
      b.innerHTML = html;
      const tm = document.createElement("span");
      tm.className = "iaw-time";
      tm.textContent = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      b.appendChild(tm);
      row.appendChild(b);
      messages.appendChild(row);
      scrollToBottom();
    }

    function configuredWelcomeMessage() {
      return welcomeMessage || t("welcome", "");
    }

    function showWelcomeMessageIfEmpty() {
      if (welcomeMessageShown || messages.children.length > 0) return;
      const text = configuredWelcomeMessage();
      if (!text) return;
      appendBubble(htmlEscape(text), "agent");
      welcomeMessageShown = true;
    }

    function configuredConnectionFailureMessage() {
      return htmlEscape(
        offlineMessage || t("connection_failed", "Falha de conexão. Tente novamente."),
      );
    }

    function formatTime(isoOrDate) {
      if (!isoOrDate) return "";
      const d = typeof isoOrDate === "string" ? new Date(normalizeWidgetTimestamp(isoOrDate)) : isoOrDate;
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }

    function getStatusIcon(status) {
      switch (status) {
        case "pending": return '<span class="iaw-tick iaw-tick-pending">🕐</span>';
        case "sent": return '<span class="iaw-tick iaw-tick-sent">✓</span>';
        case "delivered": return '<span class="iaw-tick iaw-tick-delivered">✓✓</span>';
        case "read": return '<span class="iaw-tick iaw-tick-read">✓✓</span>';
        default: return '<span class="iaw-tick iaw-tick-sent">✓</span>';
      }
    }

    function renderRichContent(content) {
      if (!content) return "";
      const locMatch = content.match(/<location\s+lat="([^"]*)" lng="([^"]*)"(?:\s+label="([^"]*)")?[^>]*\/?>/i);
      if (locMatch) {
        const [full, lat, lng, label] = locMatch;
        const mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;
        const rest = stripAgentTags(content.replace(full, "").trim());
        return `<div class="iaw-location-card"><a href="${mapUrl}" target="_blank" rel="noopener">📍 ${htmlEscape(label || "Localização")}<br><small>${lat}, ${lng}</small></a></div>` + (rest ? formatWhatsApp(rest) : "");
      }
      const contactMatch = content.match(/<contact\s+name="([^"]*)" phone="([^"]*)"[^>]*\/?>/i);
      if (contactMatch) {
        const [full, cName, cPhone] = contactMatch;
        const rest = stripAgentTags(content.replace(full, "").trim());
        return `<div class="iaw-contact-card"><b>👤 ${htmlEscape(cName)}</b><a href="tel:${cPhone}">${htmlEscape(cPhone)}</a></div>` + (rest ? formatWhatsApp(rest) : "");
      }
      return formatWhatsApp(stripAgentTags(content));
    }

    let replyingTo = null;
    let lastTypingSent = 0;
    const REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

    function setReplyingTo(msg) {
      replyingTo = msg ? { id: msg.id, content: (msg.content || "").slice(0, 80), sender: msg.sender } : null;
      const bar = document.querySelector(".iaw-reply-bar");
      if (!bar) return;
      if (replyingTo) {
        bar.style.display = "flex";
        bar.querySelector(".iaw-reply-bar-text").textContent = replyingTo.content || t("media", "Mídia");
        bar.querySelector(".iaw-reply-bar-sender").textContent = (replyingTo.sender === "user" || replyingTo.sender === "client") ? t("reply_you", "Você") : t("reply_agent", "Agente");
      } else {
        bar.style.display = "none";
      }
      input.focus();
    }

    function setupReactionPicker(row, msg) {
      let hoverTimer = null;
      const showPicker = () => {
        let picker = row.querySelector(".iaw-reaction-picker");
        if (picker) { picker.style.display = "flex"; return; }
        picker = document.createElement("div");
        picker.className = "iaw-reaction-picker";
        REACTION_EMOJIS.forEach((emoji) => {
          const btn = document.createElement("button");
          btn.textContent = emoji;
          btn.onclick = async (e) => {
            e.stopPropagation();
            picker.style.display = "none";
            if (session) {
              try {
                await sendReaction(
                  session.id,
                  publicKey,
                  sessionVisitorToken(),
                  msg.id,
                  emoji,
                );
              } catch {}
            }
            let reactionsEl = row.querySelector(".iaw-reactions");
            if (!reactionsEl) {
              reactionsEl = document.createElement("div");
              reactionsEl.className = "iaw-reactions";
              row.querySelector(".iaw-bubble").appendChild(reactionsEl);
            }
            const badge = document.createElement("span");
            badge.className = "iaw-reaction-badge";
            badge.textContent = emoji;
            reactionsEl.appendChild(badge);
          };
          picker.appendChild(btn);
        });
        row.querySelector(".iaw-bubble").appendChild(picker);
      };
      row.addEventListener("mouseenter", () => { hoverTimer = setTimeout(showPicker, 600); });
      row.addEventListener("mouseleave", () => { clearTimeout(hoverTimer); const p = row.querySelector(".iaw-reaction-picker"); if (p) p.style.display = "none"; });
      let touchTimer = null;
      row.addEventListener("touchstart", () => { touchTimer = setTimeout(showPicker, 500); }, { passive: true });
      row.addEventListener("touchend", () => clearTimeout(touchTimer));
      row.addEventListener("touchmove", () => clearTimeout(touchTimer));
    }

    function updateMessageStatus(messageId, status) {
      const row = messages.querySelector(`.iaw-row[data-message-id="${messageId}"]`);
      if (!row) return;
      const tick = row.querySelector(".iaw-status");
      if (tick) {
        tick.dataset.status = status;
        tick.innerHTML = getStatusIcon(status);
      }
    }

    function updateMessageReaction(messageId, emoji) {
      const row = messages.querySelector(`.iaw-row[data-message-id="${messageId}"]`);
      if (!row) return;
      let reactionsEl = row.querySelector(".iaw-reactions");
      if (!reactionsEl) {
        reactionsEl = document.createElement("div");
        reactionsEl.className = "iaw-reactions";
        row.querySelector(".iaw-bubble").appendChild(reactionsEl);
      }
      const badge = document.createElement("span");
      badge.className = "iaw-reaction-badge";
      badge.textContent = emoji;
      reactionsEl.appendChild(badge);
    }

    function showTypingIndicator() {
      if (!expectingAgentReply) return;
      const el = document.querySelector(".iaw-typing");
      if (!el) return;
      el.innerHTML = t("typing", "Agente digitando") + '<span class="iaw-typing-dots"><span>.</span><span>.</span><span>.</span></span>';
      el.style.display = "block";
      scrollToBottom();
      clearTimeout(typingTimeoutRef);
      typingTimeoutRef = setTimeout(() => {
        if (!expectingAgentReply) el.style.display = "none";
      }, 4000);
    }

    function isInternalToolMessage(msg) {
      if (!msg) return true;
      const sender = String(msg.sender || "").toLowerCase();
      const senderType = String(msg.sender_type || "").toLowerCase();
      if (sender === "function" || sender === "tool" || senderType === "function" || senderType === "tool")
        return true;
      const content = String(msg.content || "").trim();
      if (/^<tool_result\b/i.test(content)) return true;
      if (/^<tool_call\b/i.test(content)) return true;
      return false;
    }

    function getMsgTime(msg) {
      const raw = msg && (msg.timestamp || msg.created_at || msg.createdAt);
      if (!raw) return 0;
      const t = new Date(raw).getTime();
      return Number.isFinite(t) ? t : 0;
    }

    function normalizePendingText(value) {
      return String(value || "").replace(/\s+/g, " ").trim();
    }

    function rememberMessageInCache(msg) {
      if (!msg || msg.id == null) return;
      if (!widgetMessageCache.find((m) => m.id === msg.id)) {
        widgetMessageCache = getSortedWidgetMessages([
          ...widgetMessageCache,
          msg,
        ]);
      }
    }

    function takeMatchingOptimisticRow(msg) {
      if (!msg || msg.id == null) return null;
      const who = msg.sender === "user" || msg.sender === "client" ? "me" : "agent";
      if (who !== "me") return null;
      const content = normalizePendingText(msg.content);
      if (!content) return null;

      for (const [tempId, pending] of pendingOptimisticMessages.entries()) {
        if (!pending || !pending.row || !pending.row.isConnected) {
          pendingOptimisticMessages.delete(tempId);
          continue;
        }
        const closeInTime = Date.now() - pending.ts < 120000;
        if (closeInTime && pending.text === content) {
          pendingOptimisticMessages.delete(tempId);
          return pending.row;
        }
      }
      return null;
    }

    function appendMessageFromApi(msg, opts) {
      const noScroll = opts && opts.noScroll;
      const skipCache = opts && opts.skipCache;
      if (msg.id && knownMessageIds.has(msg.id)) return;
      const optimisticRow = takeMatchingOptimisticRow(msg);
      if (optimisticRow) {
        reconcileOptimisticRow(optimisticRow, msg);
        if (!skipCache) rememberMessageInCache(msg);
        if (!noScroll) scrollToBottom();
        return;
      }
      if (isInternalToolMessage(msg)) {
        if (msg.id) knownMessageIds.add(msg.id);
        return;
      }
      const who = msg.sender === "user" || msg.sender === "client" ? "me" : "agent";
      const isAgentMsg =
        who === "agent" &&
        (msg.sender === "agent" ||
          msg.sender === "ai" ||
          msg.sender === "human" ||
          msg.sender_type === "agent" ||
          msg.sender_type === "ai" ||
          msg.sender_type === "human");
      if (isAgentMsg) hideTyping();
      let html = "";
      if (msg.quoted_content) {
        html += `<div class="iaw-quoted"><b class="iaw-quoted-sender">${htmlEscape(msg.quoted_sender || "")}</b><span>${htmlEscape(stripAgentTags(msg.quoted_content))}</span></div>`;
      }
      if (msg.media_type && msg.media_url) {
        const src = absMedia(msg.media_url);
        if (msg.media_type === "image")
          html += `<div class="iaw-media"><img src="${src}" alt="imagem"/></div>`;
        else if (msg.media_type === "video")
          html += `<div class="iaw-media"><video src="${src}" controls></video></div>`;
        else if (msg.media_type === "audio" || msg.media_type === "voice")
          html += `<div class="iaw-media iaw-media-audio"><audio src="${src}" controls preload="metadata"></audio>${msg.transcription ? `<p class="iaw-transcription">${htmlEscape(msg.transcription)}</p>` : ""}</div>`;
        else
          html += `<div class="iaw-doc-card"><span>📄 ${htmlEscape(msg.filename || "Documento")}</span><a href="${src}" target="_blank" class="iaw-doc-download">⬇ Download</a></div>`;
      } else if (msg.interactive_data) {
        const parsed = msg.interactive_data;
        if (parsed.header) {
          if (parsed.header.type === "image" && parsed.header.url) {
            html += `<div class="iaw-media"><img src="${absMedia(parsed.header.url)}" alt="header"/></div>`;
          } else if (parsed.header.type === "video" && parsed.header.url) {
            html += `<div class="iaw-media"><video src="${absMedia(parsed.header.url)}" controls></video></div>`;
          } else if (parsed.header.type === "document" && parsed.header.url) {
            html += `<div class="iaw-doc-card"><span>📄 Documento</span><a href="${absMedia(parsed.header.url)}" target="_blank" class="iaw-doc-download">⬇ Download</a></div>`;
          } else if (parsed.header.type === "text" && parsed.header.value) {
            html += `<div class="iaw-interactive-header"><strong>${htmlEscape(parsed.header.value)}</strong></div>`;
          }
        }
        if (parsed.body) {
          html += renderRichContent(parsed.body);
        }
        if (parsed.footer) {
          html += `<div class="iaw-interactive-footer"><small>${htmlEscape(parsed.footer)}</small></div>`;
        }

        html += `<div class="iaw-interactive-actions">`;
        if (parsed.subtype === "button" && parsed.buttons) {
          parsed.buttons.forEach((btn) => {
            html += `<button type="button" class="iaw-interactive-btn" data-reply-id="${htmlEscape(btn.id)}">${htmlEscape(btn.title)}</button>`;
          });
        } else if (parsed.subtype === "list" && parsed.sections) {
          html += `<button type="button" class="iaw-interactive-list-btn" data-list-toggle="1">${htmlEscape(parsed.list_button || "Opções")}</button>`;
          html += `<div class="iaw-interactive-list-menu">`;
          parsed.sections.forEach((sec) => {
            if (sec.title) html += `<div class="iaw-interactive-list-sec">${htmlEscape(sec.title)}</div>`;
            (sec.rows || []).forEach((row) => {
              html += `<div class="iaw-interactive-list-row" data-reply-id="${htmlEscape(row.id)}">
                <div class="iaw-interactive-list-row-title">${htmlEscape(row.title)}</div>
                ${row.description ? `<div class="iaw-interactive-list-row-desc">${htmlEscape(row.description)}</div>` : ""}
              </div>`;
            });
          });
          html += `</div>`;
        } else if (parsed.subtype === "cta_url" && parsed.cta) {
          html += `<a href="${htmlEscape(parsed.cta.url)}" target="_blank" rel="noopener" class="iaw-interactive-cta">${htmlEscape(parsed.cta.display_text)}</a>`;
        } else if (parsed.subtype === "flow" && parsed.flow) {
          html += `<button type="button" class="iaw-interactive-flow-btn" data-flow-id="${htmlEscape(parsed.flow.flow_id)}">${htmlEscape(parsed.flow.flow_cta || "Abrir")}</button>`;
        }
        html += `</div>`;
      } else {
        html += renderRichContent(msg.content || "");
      }
      const row = document.createElement("div");
      row.className = `iaw-row iaw-${who}`;
      row.dataset.messageId = String(msg.id);
      const msgTs = getMsgTime(msg);
      row.dataset.ts = String(msgTs);
      row.dataset.mid = String(msg.id || 0);
      const b = document.createElement("div");
      b.className = `iaw-bubble ${msg.media_url ? "iaw-media" : ""}`.trim();
      b.innerHTML = html;
      const tm = document.createElement("span");
      tm.className = "iaw-time";
      tm.textContent = formatTime(msg.timestamp);
      if (who === "me") {
        const tick = document.createElement("span");
        tick.className = "iaw-status";
        tick.dataset.status = msg.status || "sent";
        tick.innerHTML = getStatusIcon(msg.status || "sent");
        tm.appendChild(tick);
      }
      b.appendChild(tm);
      if (msg.reactions && msg.reactions.length) {
        const reactionsEl = document.createElement("div");
        reactionsEl.className = "iaw-reactions";
        const counts = {};
        msg.reactions.forEach((r) => { counts[r.emoji] = (counts[r.emoji] || 0) + 1; });
        Object.entries(counts).forEach(([emoji, count]) => {
          const badge = document.createElement("span");
          badge.className = "iaw-reaction-badge";
          badge.textContent = count > 1 ? `${emoji} ${count}` : emoji;
          reactionsEl.appendChild(badge);
        });
        b.appendChild(reactionsEl);
      }
      row.appendChild(b);

      const listToggle = b.querySelector(".iaw-interactive-list-btn[data-list-toggle]");
      if (listToggle) {
        listToggle.onclick = () => {
          const menu = listToggle.nextElementSibling;
          if (menu) menu.classList.toggle("iaw-show");
        };
      }
      b.querySelectorAll(".iaw-interactive-btn, .iaw-interactive-list-row").forEach((btn) => {
        btn.onclick = () => {
          const replyId = btn.dataset.replyId;
          const title =
            btn.querySelector(".iaw-interactive-list-row-title")?.textContent?.trim() ||
            btn.textContent?.trim() ||
            replyId;
          sendInteractiveReply(title, replyId);
        };
      });

      const replyBtn = document.createElement("button");
      replyBtn.className = "iaw-reply-btn";
      replyBtn.textContent = "↩";
      replyBtn.title = t("reply", "Responder");
      replyBtn.onclick = () => setReplyingTo(msg);
      row.appendChild(replyBtn);
      setupReactionPicker(row, msg);
      insertRowChronologically(row, msgTs, Number(msg.id || 0));
      knownMessageIds.add(msg.id);
      if (!skipCache && msg && msg.id != null) {
        rememberMessageInCache(msg);
      }
      if (!noScroll) scrollToBottom();
    }

    function insertRowChronologically(row, ts, mid) {
      const children = messages.children;
      if (!children.length) {
        messages.appendChild(row);
        return;
      }
      const last = children[children.length - 1];
      const lastTs = Number(last.dataset.ts || 0);
      const lastMid = Number(last.dataset.mid || 0);
      if (ts > lastTs || (ts === lastTs && mid >= lastMid)) {
        messages.appendChild(row);
        return;
      }
      let inserted = false;
      for (let i = 0; i < children.length; i++) {
        const c = children[i];
        const cTs = Number(c.dataset.ts || 0);
        const cMid = Number(c.dataset.mid || 0);
        if (ts < cTs || (ts === cTs && mid < cMid)) {
          messages.insertBefore(row, c);
          inserted = true;
          break;
        }
      }
      if (!inserted) messages.appendChild(row);
    }

    async function loadHistory() {
      if (!session) return;
      try {
        const pack = await getWidgetMessages(
          session.id,
          publicKey,
          sessionVisitorToken(),
          { limit: 100, order: "desc" },
        );
        const list = pack.messages || [];
        widgetMessageCache = getSortedWidgetMessages(list);
        hasMoreWidgetHistory = list.length >= 100;
        applySessionClosedState(pack.session_status);
        knownMessageIds = new Set();
        messages.innerHTML = "";
        welcomeMessageShown = false;
        for (const msg of widgetMessageCache) {
          appendMessageFromApi(msg, { noScroll: true, skipCache: true });
        }
        showWelcomeMessageIfEmpty();
        scrollToBottom();
      } catch (err) {
        if (err && err.status === 403) resetWidgetSession();
      }
    }

    async function loadOlderWidgetMessages() {
      if (
        !session ||
        !hasMoreWidgetHistory ||
        loadingOlderWidget ||
        !widgetMessageCache.length
      ) {
        return;
      }
      loadingOlderWidget = true;
      const oldest = widgetMessageCache[0];
      const before = oldest && oldest.timestamp;
      if (!before) {
        loadingOlderWidget = false;
        return;
      }
      const prevH = messages.scrollHeight;
      const prevT = messages.scrollTop;
      try {
        const pack = await getWidgetMessages(
          session.id,
          publicKey,
          sessionVisitorToken(),
          { before: String(before), limit: 50, order: "desc" },
        );
        const batch = pack.messages || [];
        if (!batch.length) {
          hasMoreWidgetHistory = false;
          return;
        }
        const batchAsc = getSortedWidgetMessages(batch);
        const seen = new Set(widgetMessageCache.map((m) => m.id));
        const dedup = batchAsc.filter((m) => !seen.has(m.id));
        if (!dedup.length) {
          hasMoreWidgetHistory = false;
          return;
        }
        widgetMessageCache = getSortedWidgetMessages([
          ...dedup,
          ...widgetMessageCache,
        ]);
        if (batch.length < 50) hasMoreWidgetHistory = false;
        knownMessageIds = new Set();
        messages.innerHTML = "";
        for (const msg of widgetMessageCache) {
          appendMessageFromApi(msg, { noScroll: true, skipCache: true });
        }
        requestAnimationFrame(() => {
          const delta = messages.scrollHeight - prevH;
          messages.scrollTop = prevT + delta;
        });
      } catch (_) {
        // keep state
      } finally {
        loadingOlderWidget = false;
      }
    }

    messages.addEventListener("scroll", () => {
      if (
        messages.scrollTop < 100 &&
        hasMoreWidgetHistory &&
        !loadingOlderWidget &&
        session
      ) {
        loadOlderWidgetMessages();
      }
    });

    function getPollingDelay() {
      const now = Date.now();
      if (expectingAgentReply) return 700;
      if (now < fastPollUntil) return 1000;
      if (now - lastWidgetActivityAt < 30000) return wsConn ? 2500 : 1800;
      return wsConn ? 12000 : 5000;
    }

    function scheduleNextPoll(delay = getPollingDelay()) {
      if (pollInterval) clearTimeout(pollInterval);
      if (panel.style.display !== "flex" || !session) {
        pollInterval = null;
        return;
      }
      pollInterval = setTimeout(async () => {
        await pollLatestMessages();
        scheduleNextPoll();
      }, Math.max(250, delay));
    }

    function pulsePollingSoon(delay = 0, windowMs = 20000) {
      fastPollUntil = Math.max(fastPollUntil, Date.now() + windowMs);
      if (panel.style.display === "flex" && session) {
        scheduleNextPoll(delay);
      }
    }

    async function pollLatestMessages() {
      if (panel.style.display !== "flex" || !session || pollingInFlight) return;
      pollingInFlight = true;
      try {
        let pack;
        if (widgetMessageCache.length > 0) {
          const latest = widgetMessageCache[widgetMessageCache.length - 1];
          if (latest && latest.id != null) {
            pack = await getWidgetMessages(
              session.id,
              publicKey,
              sessionVisitorToken(),
              { afterId: latest.id, order: "asc", limit: 100 },
            );
          }
        }
        if (!pack) {
          pack = await getWidgetMessages(
            session.id,
            publicKey,
            sessionVisitorToken(),
            { limit: 30, order: "desc" },
          );
        }
        applySessionClosedState(pack.session_status);
        const list = pack.messages || [];
        for (const msg of list) {
          if (knownMessageIds.has(msg.id)) continue;
          appendMessageFromApi(msg);
        }
        if (list.length > 0) {
          lastWidgetActivityAt = Date.now();
          fastPollUntil = Math.max(fastPollUntil, Date.now() + 5000);
        }
      } catch (err) {
        if (err && err.status === 403) {
          resetWidgetSession();
        }
      } finally {
        pollingInFlight = false;
      }
    }

    function startPolling() {
      stopPolling();

      // WebSocket real-time (primário)
      if (session && !wsConn) {
        try {
          wsConn = connectWidgetWebSocket(
            session.id,
            publicKey,
            sessionVisitorToken(),
            {
            onMessage: (msg) => {
              if (msg && !knownMessageIds.has(msg.id)) {
                lastWidgetActivityAt = Date.now();
                appendMessageFromApi(msg);
                pulsePollingSoon(1500, 5000);
              }
            },
            onReaction: (data) => {
              lastWidgetActivityAt = Date.now();
              updateMessageReaction(data.message_id, data.emoji);
            },
            onStatusUpdate: (data) => {
              lastWidgetActivityAt = Date.now();
              updateMessageStatus(data.message_id, data.status);
            },
            onTyping: () => {
              lastWidgetActivityAt = Date.now();
              pulsePollingSoon(500, 10000);
              showTypingIndicator();
            },
          },
          );
        } catch (_) {}
      }

      scheduleNextPoll(500);
    }
    function stopPolling() {
      if (pollInterval) clearTimeout(pollInterval);
      pollInterval = null;
      pollingInFlight = false;
      if (wsConn) {
        wsConn.close();
        wsConn = null;
      }
    }

    async function sendInteractiveReply(text, replyId) {
      if (!text) return;
      if (sessionEnded) return;
      setReplyingTo(null);

      const tempId = `temp_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
      const optimisticRow = appendOptimisticTextBubble(tempId, text, {});
      expectingAgentReply = true;
      setTyping(true);
      lastWidgetActivityAt = Date.now();
      pulsePollingSoon(0, 30000);

      (async () => {
        try {
          await ensureSession();
          if (!pollInterval && panel.style.display === "flex") {
            startPolling();
          } else {
            pulsePollingSoon(0, 30000);
          }
          const payload = {
            session_id: session.id,
            visitor_token: sessionVisitorToken(),
            content: text,
            public_key: publicKey,
            interactive_reply_id: replyId
          };
          const rtMsg = await getRecaptchaToken("widget_message");
          if (rtMsg) payload.recaptcha_token = rtMsg;
          const data = await sendWidgetMessage(payload);
          if (data && data.message) {
            reconcileOptimisticRow(optimisticRow, data.message);
          }
          pulsePollingSoon(350, 30000);
        } catch (e) {
          hideTyping();
          markOptimisticRowFailed(optimisticRow);
          appendBubble(configuredConnectionFailureMessage(), "system");
        }
      })();
    }

    async function sendText() {
      const text = (input.value || "").trim();
      if (!text) return;
      if (sessionEnded) return;
      input.value = "";
      const quoteData = replyingTo
        ? {
            quoted_message_id: replyingTo.id,
            quoted_content: replyingTo.content,
            quoted_sender: replyingTo.sender,
          }
        : {};
      setReplyingTo(null);

      const tempId = `temp_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
      const optimisticRow = appendOptimisticTextBubble(tempId, text, quoteData);
      expectingAgentReply = true;
      setTyping(true);
      lastWidgetActivityAt = Date.now();
      pulsePollingSoon(0, 30000);

      (async () => {
        try {
          await ensureSession();
          if (!pollInterval && panel.style.display === "flex") {
            startPolling();
          } else {
            pulsePollingSoon(0, 30000);
          }
          const payload = {
            session_id: session.id,
            visitor_token: sessionVisitorToken(),
            content: text,
            public_key: publicKey,
            ...quoteData,
          };
          const rtMsg = await getRecaptchaToken("widget_message");
          if (rtMsg) payload.recaptcha_token = rtMsg;
          const data = await sendWidgetMessage(payload);
          if (data && data.message) {
            reconcileOptimisticRow(optimisticRow, data.message);
          }
          pulsePollingSoon(350, 30000);
        } catch (e) {
          hideTyping();
          markOptimisticRowFailed(optimisticRow);
          appendBubble(configuredConnectionFailureMessage(), "system");
        }
      })();
    }

    function pickAudioMime() {
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/ogg",
        "audio/mp4",
        "audio/mpeg",
      ];
      if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported)
        return "";
      for (const m of candidates) {
        try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (_) {}
      }
      return "";
    }

    function mimeToExt(mime) {
      const m = String(mime || "").toLowerCase();
      if (m.includes("webm")) return "webm";
      if (m.includes("ogg")) return "ogg";
      if (m.includes("mp4")) return "m4a";
      if (m.includes("mpeg")) return "mp3";
      if (m.includes("wav")) return "wav";
      return "bin";
    }

    function buildOptimisticMediaHtml(file, src) {
      const type = String(file.type || "").toLowerCase();
      if (type.startsWith("audio/"))
        return `<div class="iaw-media iaw-media-audio"><audio src="${src}" controls preload="metadata"></audio></div>`;
      if (type.startsWith("video/"))
        return `<div class="iaw-media"><video src="${src}" controls></video></div>`;
      if (type.startsWith("image/"))
        return `<div class="iaw-media"><img src="${src}" alt="imagem"/></div>`;
      return `<div class="iaw-doc-card"><span>📄 ${htmlEscape(file.name || "Arquivo")}</span></div>`;
    }

    function appendOptimisticTextBubble(tempId, text, quoteData) {
      const ts = Date.now();
      const row = document.createElement("div");
      row.className = "iaw-row iaw-me";
      row.dataset.tempId = tempId;
      row.dataset.ts = String(ts);
      row.dataset.mid = String(Number.MAX_SAFE_INTEGER);
      const b = document.createElement("div");
      b.className = "iaw-bubble";
      let html = "";
      if (quoteData && quoteData.quoted_content) {
        html += `<div class="iaw-quoted"><b class="iaw-quoted-sender">${htmlEscape(
          quoteData.quoted_sender || "",
        )}</b><span>${htmlEscape(quoteData.quoted_content)}</span></div>`;
      }
      html += renderRichContent(text || "");
      b.innerHTML = html;
      const tm = document.createElement("span");
      tm.className = "iaw-time";
      tm.textContent = formatTime(new Date());
      const tick = document.createElement("span");
      tick.className = "iaw-status";
      tick.dataset.status = "pending";
      tick.innerHTML = getStatusIcon("pending");
      tm.appendChild(tick);
      b.appendChild(tm);
      row.appendChild(b);
      insertRowChronologically(row, ts, Number.MAX_SAFE_INTEGER);
      pendingOptimisticMessages.set(tempId, {
        row,
        text: normalizePendingText(text),
        ts,
      });
      scrollToBottom();
      return row;
    }

    function reconcileOptimisticRow(row, msg) {
      if (!row || !msg || msg.id == null) return;
      if (row.dataset.tempId) {
        pendingOptimisticMessages.delete(row.dataset.tempId);
      }
      const idStr = String(msg.id);
      const alreadyRendered = messages.querySelector(
        `.iaw-row[data-message-id="${idStr}"]:not([data-temp-id])`,
      );
      if (alreadyRendered && alreadyRendered !== row) {
        try { row.remove(); } catch (_) {}
        return;
      }
      knownMessageIds.add(msg.id);
      rememberMessageInCache(msg);
      row.dataset.messageId = idStr;
      delete row.dataset.tempId;
      row.dataset.mid = idStr;
      const tickEl = row.querySelector(".iaw-status");
      if (tickEl) {
        const status = msg.status || "sent";
        tickEl.dataset.status = status;
        tickEl.innerHTML = getStatusIcon(status);
      }
    }

    function markOptimisticRowFailed(row) {
      if (!row) return;
      if (row.dataset.tempId) {
        pendingOptimisticMessages.delete(row.dataset.tempId);
      }
      const tickEl = row.querySelector(".iaw-status");
      if (tickEl) tickEl.innerHTML = "⚠";
      row.classList.add("iaw-row-failed");
    }

    function appendOptimisticMediaBubble(tempId, file, src) {
      const ts = Date.now();
      const row = document.createElement("div");
      row.className = "iaw-row iaw-me";
      row.dataset.tempId = tempId;
      row.dataset.ts = String(ts);
      row.dataset.mid = String(Number.MAX_SAFE_INTEGER);
      const b = document.createElement("div");
      b.className = `iaw-bubble iaw-media ${
        String(file.type || "").startsWith("audio/") ? "iaw-bubble-audio" : ""
      }`.trim();
      b.innerHTML = buildOptimisticMediaHtml(file, src);
      const tm = document.createElement("span");
      tm.className = "iaw-time";
      tm.textContent = formatTime(new Date());
      const tick = document.createElement("span");
      tick.className = "iaw-status";
      tick.dataset.status = "pending";
      tick.innerHTML = getStatusIcon("pending");
      tm.appendChild(tick);
      b.appendChild(tm);
      row.appendChild(b);
      insertRowChronologically(row, ts, Number.MAX_SAFE_INTEGER);
      scrollToBottom();
      return row;
    }

    async function uploadFile(file) {
      if (sessionEnded) return;
      await ensureSession();
      if (!pollInterval && panel.style.display === "flex") startPolling();
      const tempId = `temp_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
      let blobUrl = null;
      try { blobUrl = URL.createObjectURL(file); } catch (_) {}
      const optimisticRow = blobUrl
        ? appendOptimisticMediaBubble(tempId, file, blobUrl)
        : null;
      try {
        const msg = await uploadWidgetMedia(
          session.id,
          publicKey,
          sessionVisitorToken(),
          file,
        );
        if (msg && msg.id != null) {
          if (optimisticRow) {
            reconcileOptimisticRow(optimisticRow, msg);
          } else {
            appendMessageFromApi({ ...msg, sender: "user" });
          }
        }
        expectingAgentReply = true;
        setTyping(true);
        lastWidgetActivityAt = Date.now();
        pulsePollingSoon(0, 30000);
      } catch (e) {
        markOptimisticRowFailed(optimisticRow);
        if (blobUrl) {
          try { URL.revokeObjectURL(blobUrl); } catch (_) {}
        }
        throw e;
      }
    }

    async function startRecording() {
      if (recording) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const mime = pickAudioMime();
        mediaRecorder = mime
          ? new MediaRecorder(stream, { mimeType: mime })
          : new MediaRecorder(stream);
        const usedMime = mediaRecorder.mimeType || mime || "audio/webm";
        const ext = mimeToExt(usedMime);
        chunks = [];
        mediaRecorder.ondataavailable = (e) => {
          if (e.data && e.data.size) chunks.push(e.data);
        };
        mediaRecorder.onstop = async () => {
          try {
            stream.getTracks().forEach((tr) => tr.stop());
          } catch (_) {}
          const blob = new Blob(chunks, { type: usedMime });
          const file = new File([blob], `gravacao_${Date.now()}.${ext}`, {
            type: usedMime,
          });
          try {
            await uploadFile(file);
          } catch (e) {
            appendBubble(
              t("audio_send_failed", "Falha ao enviar áudio."),
              "system",
            );
          }
        };
        mediaRecorder.start();
        recording = true;
        btnMic.classList.add("is-recording");
        btnMic.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>';
        const stopLabel = t("stop_recording", "Parar e enviar");
        btnMic.title = stopLabel;
        btnMic.setAttribute("aria-label", stopLabel);
      } catch (e) {
        appendBubble(
          t("mic_denied", "Não foi possível acessar o microfone."),
          "system",
        );
      }
    }

    function stopRecording() {
      if (!recording || !mediaRecorder) return;
      recording = false;
      btnMic.classList.remove("is-recording");
      btnMic.innerHTML = SVG_MIC;
      const recordLabel = t("record", "Gravar áudio");
      btnMic.title = recordLabel;
      btnMic.setAttribute("aria-label", recordLabel);
      try {
        mediaRecorder.stop();
      } catch {}
    }

    // Events
    preformSubmitBtn.addEventListener("click", async () => {
      const profile = getVisitorProfile();
      const preformMsg = (preformMessageInput.value || "").trim();

      const cfValues = {};
      for (const inp of customFieldInputs) {
        cfValues[inp.dataset.cfKey] = (inp.value || "").trim();
      }

      const nextProfile = {
        ...profile,
        name: String(preformNameInput.value || profile.name || "").trim(),
        phone: String(preformPhoneInput.value || profile.phone || "").trim(),
        email: String(preformEmailInput.value || profile.email || "").trim(),
        _preformMessage: preformMsg,
        _customFields: cfValues,
      };
      if (collectName && !nextProfile.name) {
        appendBubble(t("preform_missing_name", "Informe seu nome."), "system");
        return;
      }
      if (collectPhone && !nextProfile.phone) {
        appendBubble(t("preform_missing_phone", "Informe seu telefone."), "system");
        return;
      }
      if (collectEmail && !nextProfile.email) {
        appendBubble(t("preform_missing_email", "Informe seu e-mail."), "system");
        return;
      }
      for (const cf of customFields) {
        if (cf.required && !cfValues[cf.key]) {
          appendBubble(
            t("preform_missing_field", `Informe: ${cf.label}`).replace("{field}", cf.label),
            "system",
          );
          return;
        }
      }
      try {
        await ensureSession(nextProfile);
        syncPreformVisibility(nextProfile);
        await loadHistory();
        startPolling();
      } catch (_) {
        appendBubble(configuredConnectionFailureMessage(), "system");
      }
    });

    async function openWidgetPanel({ autoStartSession = false, sendInitialMessage = null } = {}) {
      panel.style.display = "flex";
      const profile = getVisitorProfile();
      syncPreformVisibility(profile);

      const needsPreform = requiresPreform(profile);
      const messageToUse = String(sendInitialMessage || configuredDefaultMessage || "").trim();

      if (needsPreform) {
        if (messageToUse && !preformMessageInput.value) {
          preformMessageInput.value = messageToUse;
        }
        return;
      }

      if (session) {
        await loadHistory();
        startPolling();
        if (sendInitialMessage && !sessionEnded) {
          input.value = sendInitialMessage;
          try { sendText(); } catch (_) {}
        } else if (!input.value && configuredDefaultMessage) {
          input.value = configuredDefaultMessage;
        }
        return;
      }

      if (autoStartSession) {
        const startProfile =
          messageToUse && !sendInitialMessage && !profile._preformMessage
            ? { ...profile, _preformMessage: messageToUse }
            : profile;
        const readySession = await ensureSession(startProfile);
        if (!readySession) return;
        await loadHistory();
        startPolling();
        if (sendInitialMessage && !sessionEnded) {
          input.value = sendInitialMessage;
          try { sendText(); } catch (_) {}
        }
        return;
      }

      if (!input.value && configuredDefaultMessage) {
        input.value = configuredDefaultMessage;
      }
      showWelcomeMessageIfEmpty();
    }

    launcher.addEventListener("click", async () => {
      const isOpen = panel.style.display === "flex";
      if (isOpen) {
        panel.style.display = "none";
        stopPolling();
        return;
      }
      try {
        await openWidgetPanel({ autoStartSession: autoStart });
      } catch (_) {}
    });
    const closeBtn = header.querySelector(".iaw-close");
    if (closeBtn && !hideClose) {
      closeBtn.addEventListener("click", () => {
        panel.style.display = "none";
        stopPolling();
      });
    }
    btnSend.addEventListener("click", sendText);
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendText();
      }
    });
    btnAttach.addEventListener("click", () => fileInput.click());
    fileInput.addEventListener("change", async (e) => {
      const files = Array.from(e.target.files || []);
      for (const f of files) {
        try {
          await uploadFile(f);
        } catch {
          appendBubble(
            t("upload_failed", "Falha ao enviar arquivo."),
            "system",
          );
        }
      }
      fileInput.value = "";
    });
    btnEmoji.addEventListener("click", () => {
      const visible = emojiPanel.style.display === "flex";
      emojiPanel.style.display = visible ? "none" : "flex";
    });
    btnMic.addEventListener("click", () => {
      if (liveCallHandle) return;
      if (!recording) startRecording();
      else stopRecording();
    });
    btnLive.addEventListener("click", () => {
      startLiveVoice().catch(() => {});
    });
    input.addEventListener("input", () => {
      const now = Date.now();
      if (now - lastTypingSent > 3000 && wsConn) {
        wsConn.send({ type: "typing" });
        lastTypingSent = now;
      }
    });

    if (widgetTriggers && widgetTriggers.length > 0) {
      initTriggerListeners(widgetTriggers, panel, launcher);
    }

    const shouldAutoOpen =
      openOnLoad || autoOpenDelay > 0 || urlInitial.open || urlInitial.message;
    if (shouldAutoOpen) {
      if (openOnLoad || urlInitial.open || urlInitial.message) {
        panel.style.display = "flex";
      }
      const delayMs =
        openOnLoad || urlInitial.open || urlInitial.message
          ? 0
          : autoOpenDelay * 1000;
      setTimeout(() => {
        (async () => {
          try {
            await openWidgetPanel({
              autoStartSession: autoStart || !!urlInitial.message,
              sendInitialMessage: urlInitial.message,
            });
          } catch (_) {}
        })();
      }, delayMs);
    }
  });
})();
